import { networkErrorApiWrapper } from '../lib/NetworkErrorHandler';
import { urnApiWrapper } from '../lib/urnApiWrapper';
import { config } from '../../config';
// @ts-nocheck
export type ThenArg<T> =
  T extends Promise<infer U>
    ? U
    : T extends (...args: any[]) => Promise<infer U>
      ? U
      : T;

type QueryParameters = {
  [key: string]:
    | string
    | number
    | string[]
    | number[]
    | boolean
    | boolean[]
    | undefined;
};

export type AiDecisionCategoryRef = {
  categoryId: string;
  name: string;
};

export type AiDecisionCategorization = {
  suggested?: AiDecisionCategoryRef;
  applied?: AiDecisionCategoryRef;
  confidence?: number;
  needsManualReview?: boolean;
  alternatives: Array<{
    categoryId?: string;
    name?: string;
    score: number;
  }>;
  current: Array<AiDecisionCategoryRef>;
  taxonomyHash?: string;
};

export type AiDecisionModeration = {
  explicit: boolean;
  labels: Array<{
    label: string;
    score?: number;
    source?: string;
  }>;
  spans: Array<{
    label?: string;
    text: string;
  }>;
  currentExplicit: boolean;
  linked: boolean;
};

export type AiDecisionDuplicates = {
  mediaCount: number;
  candidates: Array<{
    reportId: string;
    reportTitle?: string;
    categoryId?: string;
    categoryName?: string;
    distance: number;
  }>;
  merged: boolean;
  pending: boolean;
};

export type AiDecision = {
  decisionId: string;
  type: "categories" | "explicit" | "duplicates";
  reportId: string;
  reportTitle: string;
  insertedAt: string;
  model: string;
  modelVersion?: string;
  promptVersion: string;
  dryRun: boolean;
  latencyMs?: number;
  error?: string;
  reason?: string;
  humanAgrees?: boolean;
  rawOutput?: string;
  categorization?: AiDecisionCategorization;
  moderation?: AiDecisionModeration;
  duplicates?: AiDecisionDuplicates;
};

export type AiRun = {
  runId: string;
  type: "categories" | "explicit" | "duplicates";
  reportId: string;
  reportTitle: string;
  dryRun: boolean;
  model: string;
  promptVersion: string;
  status: "queued" | "running";
  queuedAt: string;
  startedAt?: string;
};

export type AiDecisionStatsGroup = {
  type: "categories" | "explicit" | "duplicates";
  model: string;
  promptVersion: string;
  dryRun: boolean;
  runs: number;
  errors: number;
  avgLatencyMs?: number;
  humanAgreed: number;
  humanDisagreed: number;
  firstAt?: string;
  lastAt?: string;
};

export type Error = {
  statusCode: number;
  name: string;
  userinfo?: string;
};

export type News = {
  id: string;
  location?: Location;
  title?: string;
  description?: string;
  image?: string;
  date?: string;
  distance?: number;
  categories?: Array<{
    id: string;
    name: string;
  }>;
};

export type WebPushSubscription = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export type UserNotificationSettings = {
  enabled: boolean;
  interval: "daily" | "weekly" | "monthly";
  muted_kinds?: Array<NotificationKind>;
};

export type NotificationKind =
  | "report-mine"
  | "report-new"
  | "report-changes"
  | "report-assigned"
  | "report-approval"
  | "report-my-statements"
  | "report-roles"
  | "voting-new"
  | "voting-closed"
  | "news"
  | "gamification";

export type Subscription = {
  category_id: string;
  subscribed: boolean;
};

export type Notification = {
  tag: string;
  title: string;
  body?: string;
  url?: string;
  sent_at: string;
  icon?: string;
  inserted_at?: string;
  read_at?: string;
};

export type NewsCategory = {
  category_id: string;
  name: string;
  description?: string;
  parent_id?: string;
  sources?: Array<NewsSourcePath>;
};

export type NewsSourcePath = Array<string>;

export type NewsSource = {
  path: NewsSourcePath;
  news_count: number;
  categories: Array<{
    id: string;
    name: string;
  }>;
};

export type NewsFeed = {
  feed_id: number;
  name: string;
  type: "RSS" | "GENERICJSON";
  url: string;
  enabled: boolean;
  notify: boolean;
  mapped: boolean;
  category_template: string;
  id_template?: string;
  title_template?: string;
  date_field?: string;
  date_format?: string;
  source?: string;
  columns?: Array<NewsFeedColumn>;
  column_order?: Array<string>;
  news_count: number;
  last_run_at?: string;
  last_success_at?: string;
  next_run_at?: string;
  last_status?: "queued" | "running" | "ok" | "unchanged" | "error";
  last_error?: string;
  last_item_count?: number;
  progress?: NewsFeedProgress;
};

export type NewsFeedProgress = {
  phase: "fetch" | "parse" | "send" | "delete";
  done?: number;
  total?: number;
  bytes?: number;
};

export type UpdateNewsFeed = {
  url?: string;
  enabled?: boolean;
  notify?: boolean;
  category_template?: string;
  id_template?: string;
  title_template?: string;
  date_field?: string;
  date_format?: "dd.MM.yyyy" | "yyyy-MM-dd";
  column_order?: Array<string>;
};

export type NewsFeedColumn = {
  name: string;
  filled: number;
  sample?: string;
};

export type NewNewsCategory = {
  name: string;
  description?: string;
  parent_id?: string;
};

export type UpdateNewsCategory = {
  name?: string;
  description?: string;
  parent_id?: string;
};

export type AdminReportListIssue = {
  issue_id: string;
  category_id: string;
  category_name?: string;
  status: ReportStatus;
  inserted_at: string;
  has_possible_duplicates?: boolean;
  assigned_to_me?: boolean;
  assignee?: {
    id: string;
    name?: string;
    surname?: string;
  };
};

export type AdminReportListItem = {
  id: string;
  issue_id?: string;
  title: string;
  category_id?: string;
  inserted_at: string;
  processed_at?: string;
  description?: string;
  media: Array<{
    media_id?: string;
    filename: string;
    public_url: string;
    media_type: string;
    censor?: MediaCensor;
  }>;
  explicit?: boolean;
  votes_count?: number;
  issues?: Array<AdminReportListIssue>;
  status?: ReportStatus;
  location?: Location;
  submitted_at?: string;
  deadline_at?: string;
  awaiting_my_approval?: boolean;
  awaiting_my_revision?: boolean;
  overdue?: boolean;
  archived_at?: string;
  archived_by?: string;
};

export type ReportListItem = {
  id: string;
  issue_id?: string;
  title: string;
  category_id?: string;
  inserted_at: string;
  processed_at?: string;
  description?: string;
  media: Array<{
    media_id?: string;
    filename: string;
    public_url: string;
    media_type: string;
    censor?: MediaCensor;
  }>;
  explicit?: boolean;
  votes_count?: number;
};

export type MyReportListItem = {
  id: string;
  issue_id?: string;
  title: string;
  category_id?: string;
  inserted_at: string;
  processed_at?: string;
  description?: string;
  media: Array<{
    media_id?: string;
    filename: string;
    public_url: string;
    media_type: string;
    censor?: MediaCensor;
  }>;
  explicit?: boolean;
  votes_count?: number;
  accepted: boolean;
  status?: ReportStatus;
  location?: Location;
  distance?: number;
};

export type ReportDetail = {
  id: string;
  issue_id?: string;
  title: string;
  category_id?: string;
  inserted_at: string;
  processed_at?: string;
  description?: string;
  media: Array<{
    media_id?: string;
    filename: string;
    public_url: string;
    media_type: string;
    censor?: MediaCensor;
  }>;
  explicit?: boolean;
  votes_count?: number;
  location?: Location;
  status?: ReportStatus;
};

export type AdminReportDetail = {
  id: string;
  issue_id?: string;
  title: string;
  category_id?: string;
  inserted_at: string;
  processed_at?: string;
  description?: string;
  media: Array<{
    media_id?: string;
    filename: string;
    public_url: string;
    media_type: string;
    censor?: MediaCensor;
  }>;
  explicit?: boolean;
  votes_count?: number;
  issues?: Array<AdminReportListIssue>;
  status?: ReportStatus;
  location?: Location;
  submitted_at?: string;
  deadline_at?: string;
  awaiting_my_approval?: boolean;
  awaiting_my_revision?: boolean;
  overdue?: boolean;
  archived_at?: string;
  archived_by?: string;
  issuedReportCategories?: Array<string>;
};

export type ReportArchiveState = {
  archived_at: string;
  archived_by?: string;
};

export type ReportStatusItem = {
  issue_id?: string;
  status: ReportStatus;
  date: string;
  categoryName: string;
  statements?: Array<PublicIssueStatement>;
};

export type ReportIssuePossibleDuplicate = {
  id: string;
  status: ReportStatus;
  inserted_at: string;
  report_id: string;
  report_title: string;
  category_id?: string;
  category_name?: string;
  embedding_distance?: number;
  distance_meters?: number;
};

export type ReportCategory = {
  category_id: string;
  name: string;
  description?: string;
  parent_id?: string;
  users_count?: number;
  full_path?: string;
  users?: Array<{
    id: string;
    name?: string;
    surname?: string;
  }>;
  is_default: boolean;
  assignment?: boolean;
};

export type ReportStatus =
  | "open"
  | "moved"
  | "accepted"
  | "duplicate"
  | "resolved";

export type NewReportCategory = {
  name: string;
  description?: string;
  parent_id?: string;
  assignment?: boolean;
};

export type UpdateReportCategory = {
  name: string;
  description?: string;
  parent_id?: string;
  assignment?: boolean;
};

export type Location = {
  type: "Point";
  coordinates: Array<number>;
};

export type CategoryUser = {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
  can_assign: boolean;
  manager: boolean;
  can_approve: boolean;
  requires_approval: boolean;
};

export type UserDetail = {
  id: string;
  name?: string;
  surname?: string;
  email?: string;
};

export type UserCandidate = {
  client_acc_id?: string;
  acc_id?: string;
  name?: string;
  surname?: string;
};

export type MediaCensor = {
  cols: number;
  rows: number;
  cells: Array<number>;
};

export type AdminReportMedia = {
  media_id: string;
  filename: string;
  media_type: string;
  public_url: string;
  censor?: MediaCensor;
};

export type IssueStatementMedia = {
  media_id: string;
  public_url: string;
  original_filename?: string;
  media_type?: string;
};

export type IssueStatement = {
  statement_id: string;
  issue_id: string;
  text: string;
  final: boolean;
  author?: {
    name?: string;
    surname?: string;
  };
  mine?: boolean;
  approval?: StatementApproval;
  reviews?: Array<StatementReview>;
  media: Array<IssueStatementMedia>;
  inserted_at: string;
  updated_at?: string;
  edited_at?: string;
  deleted_at?: string;
  deleted_by?: {
    name?: string;
    surname?: string;
  };
  revisions?: Array<StatementRevision>;
};

export type StatementRevisionInput = {
  text: string;
  removed_media_ids?: Array<string>;
};

export type StatementRevisionResult = {
  revision_id: string;
  published: boolean;
  approval?: StatementApproval;
};

export type StatementRevision = {
  revision_id: string;
  text: string;
  previous_text: string;
  media_added: Array<IssueStatementMedia>;
  media_removed: Array<IssueStatementMedia>;
  approval?: StatementApproval;
  author?: {
    name?: string;
    surname?: string;
  };
  mine?: boolean;
  reviews: Array<StatementReview>;
  inserted_at: string;
  published_at?: string;
};

export type StatementApproval =
  | "pending"
  | "rejected"
  | "approved"
  | "withdrawn";

export type StatementReview = {
  decision: "approved" | "rejected";
  submitted_text: string;
  note?: string;
  reviewer?: {
    name?: string;
    surname?: string;
  };
  reviewed_at: string;
};

export type PublicIssueStatement = {
  statement_id: string;
  text: string;
  final: boolean;
  media: Array<IssueStatementMedia>;
  inserted_at: string;
  edited_at?: string;
};

export type Issue = {
  id: string;
  report_id: string;
  category_id: string;
  category_name: string;
  status: ReportStatus;
  inserted_at: string;
  updated_at?: string;
  parent_id?: string;
  parent_report_id?: string;
  resolved_at?: string;
  has_possible_duplicates?: boolean;
  possible_duplicates_count?: number;
  statements_count?: number;
  statement_in_review_id?: string;
  awaiting_my_approval?: boolean;
  awaiting_my_revision?: boolean;
  category_assignment?: boolean;
  assignee?: {
    id: string;
    name?: string;
    surname?: string;
  };
  assigned_at?: string;
  assigned_to_me?: boolean;
  can_assign?: boolean;
};

export type unread_count = number;

export type VotingListItem = {
  voting_id: string;
  title: string;
  description?: string;
  open_from?: string;
  open_to?: string;
  votes_count: number;
  verified_accounts_only: boolean;
  inserted_at: string;
  forgotten: boolean;
  already_voted?: boolean;
  public?: boolean;
  allow_anonymous?: boolean;
  available_results?: boolean;
};

export type VotingDetail = {
  voting_id: string;
  title: string;
  description?: string;
  open_from?: string;
  open_to?: string;
  votes_count: number;
  verified_accounts_only: boolean;
  public: boolean;
  available_results: boolean;
  available_incomplete_results: boolean;
  report_enabled?: boolean;
  report_emails?: Array<string>;
  report_sent_at?: string;
  questions: Array<VotingQuestion>;
  forgotten?: boolean;
  allow_anonymous?: boolean;
  already_voted?: boolean;
  my_votes?: Array<{
    question_id: number;
    options: Array<{
      option_id: number;
      text?: string;
      value?: number;
    }>;
  }>;
};

export type VotingQuestion = {
  question_id: number;
  voting_id: string;
  title: string;
  description?: string;
  min_options: number;
  max_options: number;
  type: "free_text" | "single_choice" | "multi_choice" | "scale";
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  options: Array<VotingQuestionOption>;
};

export type VotingQuestionWithResults = {
  question_id: number;
  voting_id: string;
  title: string;
  description?: string;
  min_options: number;
  max_options: number;
  type: "free_text" | "single_choice" | "multi_choice" | "scale";
  scale_min?: number;
  scale_max?: number;
  scale_min_label?: string;
  scale_max_label?: string;
  options: Array<{
    option_id: number;
    voting_id: string;
    question_id: number;
    title: string;
    description?: string;
    required?: boolean;
    url?: string;
    type: "choice" | "free";
    votes_count: number;
    text_answers?: Array<string>;
    average?: number;
    histogram?: Array<{
      value: number;
      count: number;
    }>;
  }>;
};

export type VotingQuestionOption = {
  option_id: number;
  voting_id: string;
  question_id: number;
  title: string;
  description?: string;
  required?: boolean;
  url?: string;
  type: "choice" | "free";
};

export type CreateVotingRequest = {
  title: string;
  description?: string;
  open_from?: string;
  open_to?: string;
  verified_accounts_only: boolean;
  public: boolean;
  allow_anonymous?: boolean;
  available_results?: boolean;
  available_incomplete_results: boolean;
  report_enabled?: boolean;
  report_emails?: Array<string>;
  questions: Array<{
    title: string;
    description?: string;
    min_options: number;
    max_options: number;
    type?: "free_text" | "single_choice" | "multi_choice" | "scale";
    scale_min?: number;
    scale_max?: number;
    scale_min_label?: string;
    scale_max_label?: string;
    options: Array<{
      title: string;
      description?: string;
      required?: boolean;
      type: "choice" | "free";
      url?: string;
    }>;
  }>;
};

export type SubmitVoteRequest = {
  votes: Array<{
    question_id: number;
    options: Array<{
      option_id: number;
      text?: string;
      value?: number;
    }>;
  }>;
  recaptchaToken?: string;
};

export type ProjectListItem = {
  project_id: string;
  title: string;
  inserted_at: string;
};

export type NewProject = {
  title: string;
  description: string;
  media?: Array<File>;
};

export type ProjectDetail = {
  project_id: string;
  title: string;
  description: string;
  media: Array<{
    media_id: string;
    public_url: string;
    original_filename?: string;
    media_type?: string;
  }>;
  inserted_at: string;
  updated_at?: string;
};

export type ProjectUpdate = {
  title?: string;
  description?: string;
  media?: Array<File>;
};

class ApiError {
  public message: string;
  public details: Error | null = null;

  constructor(message: string) {
    this.message = message;
  }
}

/**
 * Centralized Identity and Access Management
 * @class DigiobcanApi
 * @param {(string)} [domainOrOptions] - The project domain.
 */
class DigiobcanApi {
  protected baseUrl: string = "";
  protected headers: { name: string; value: string }[] = [];
  protected token: string = "";

  serializeQueryParams(parameters: QueryParameters) {
    return Object.keys(parameters)
      .reduce((acc: string[], p) => {
        const param = parameters[p];
        if (typeof param === "undefined" || param === "") {
          return acc;
        }

        if (Array.isArray(parameters[p])) {
          return [
            ...acc,
            ...parameters[p].map(
              (v) => `${encodeURIComponent(p)}=${encodeURIComponent(v)}`,
            ),
          ];
        } else {
          return [
            ...acc,
            `${encodeURIComponent(p)}=${encodeURIComponent(
              String(parameters[p]),
            )}`,
          ];
        }
      }, [])
      .join("&");
  }

  protected transformParameter(value: any, transformOperation?: string) {
    switch (transformOperation) {
      case "joinUsingPipes":
        return Array.isArray(value) ? value.join("|") : value;
      default:
        return value;
    }
  }

  public setBaseUrl(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public setHeaders(headers: { name: string; value: string }[]) {
    this.headers = headers;
  }

  public setToken(token: string) {
    this.token = token;
  }

  protected appendAuthHeaders(headerParams: Headers) {
    const headers = new Headers(headerParams);
    if (this.token) {
      headers.append("Authorization", `Bearer ${this.token}`);
    }
    return headers;
  }

  private async request(
    method: string,
    url: string,
    body: any,
    headers: Headers = new Headers(),
    queryParameters: QueryParameters = {},
  ) {
    const queryParams =
      queryParameters && Object.keys(queryParameters).length
        ? this.serializeQueryParams(queryParameters)
        : null;
    const urlWithParams = url + (queryParams ? "?" + queryParams : "");

    if (headers.get("Content-Type") === "multipart/form-data") {
      const form = new FormData();
      for (let k in body) {
        const value = body[k];
        if (Array.isArray(value)) {
          value.forEach((v) => {
            form.append(k, v);
          });
        } else {
          form.append(k, value);
        }
      }
      body = form;
    } else if (
      headers.get("Content-Type") === "application/json" &&
      body &&
      Object.keys(body).length > 0
    ) {
      body = JSON.stringify(body);
    } else {
      body = undefined;
    }

    if (headers.get("Content-Type") === "multipart/form-data") {
      headers.delete("Content-Type");
    }

    if (this.headers.length > 0) {
      this.headers.forEach((h) => {
        headers.append(h.name, h.value);
      });
    }

    const response = await fetch(urlWithParams, { method, headers, body });

    if (response.status === 204) return response;

    if (response.ok) {
      const responseContentType =
        (response.headers && response.headers.get("Content-Type")) || "";
      if (responseContentType.includes("application/json")) {
        return response.json();
      }
      return response;
    } else {
      const err = new ApiError(response.statusText);
      err.details = await response.json();
      throw err;
    }
  }

  /**
   * Get list of reports
   * @method
   * @name DigiobcanApi#meGetReports
   */
  meGetReports(
    parameters: {
      limit?: number;
      offset?: number;
      location?: string;
      orderBy?: "date" | "location" | "votes";
      orderDirection?: "asc" | "desc";
    } = {},
  ): Promise<{
    reports: Array<{
      id: string;
      issue_id?: string;
      title: string;
      category_id?: string;
      inserted_at: string;
      processed_at?: string;
      description?: string;
      media: Array<{
        media_id?: string;
        filename: string;
        public_url: string;
        media_type: string;
        censor?: MediaCensor;
      }>;
      explicit?: boolean;
      votes_count?: number;
      accepted: boolean;
      status?: ReportStatus;
      location?: Location;
      distance?: number;
    }>;
    hasNextOffset: boolean;
    offset: number;
    limit: number;
  }> {
    let path = "/me/reports";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["offset"] !== "undefined") {
      queryParameters["offset"] = parameters["offset"];
    }

    queryParameters["offset"] = this.transformParameter(
      queryParameters["offset"],
    );

    if (typeof parameters["location"] !== "undefined") {
      queryParameters["location"] = parameters["location"];
    }

    queryParameters["location"] = this.transformParameter(
      queryParameters["location"],
    );

    queryParameters["orderBy"] = "date";

    if (typeof parameters["orderBy"] !== "undefined") {
      queryParameters["orderBy"] = parameters["orderBy"];
    }

    queryParameters["orderBy"] = this.transformParameter(
      queryParameters["orderBy"],
    );

    if (typeof parameters["orderDirection"] !== "undefined") {
      queryParameters["orderDirection"] = parameters["orderDirection"];
    }

    queryParameters["orderDirection"] = this.transformParameter(
      queryParameters["orderDirection"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Kategorie podnetov prihlaseneho uradnika, teda **vsetko, co v podnetoch
vidi**. S `pruser:report-categories:access-all` alebo
`pruser:report-categories:read-all` su to vsetky kategorie, inak len tie,
ku ktorym je priradeny. Ci v kategorii smie aj konat, hovori `managed`.

    * @method
    * @name DigiobcanApi#meGetReportCategories
    */
  meGetReportCategories(parameters: {} = {}): Promise<{
    categories?: Array<{
      name: string;
      category_id: string;
      is_default: boolean;
      managed: boolean;
      manager: boolean;
      can_approve: boolean;
      requires_approval: boolean;
      can_assign: boolean;
      assignment: boolean;
    }>;
  }> {
    let path = "/me/reports-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get my news categories subscriptions
   * @method
   * @name DigiobcanApi#meGetNewsCategories
   */
  meGetNewsCategories(parameters: {} = {}): Promise<{
    subscriptions: Array<string>;
  }> {
    let path = "/me/news-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Update my news categories subscriptions
   * @method
   * @name DigiobcanApi#meUpdateNewsCategories
   */
  meUpdateNewsCategories(
    parameters: {} = {},
    body: Array<Subscription>,
  ): Promise<{
    changes: number;
  }> {
    let path = "/me/news-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Get list of all vote tokens used by the user, sorted by creation date (newest first).
Can be filtered by active status.

    * @method
    * @name DigiobcanApi#meGetVoteTokens
    */
  meGetVoteTokens(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
      active?: boolean;
    } = {},
  ): Promise<{
    tokens: Array<{
      token_id: string;
      target: string;
      inserted_at?: string;
      expires_at?: string;
      revoked_at?: string;
    }>;
    nextCursor?: {};
  }> {
    let path = "/me/vote-tokens";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    if (typeof parameters["active"] !== "undefined") {
      queryParameters["active"] = parameters["active"];
    }

    queryParameters["active"] = this.transformParameter(
      queryParameters["active"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Generate votes for a target
   * @method
   * @name DigiobcanApi#meVoteToken
   */
  meVoteToken(
    parameters: {} = {},
    body: {
      target: string;
      count: number;
    },
  ): Promise<{}> {
    let path = "/me/vote-tokens";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * How the signed-in citizen stands on this one target: how many of their votes
are on it, and how many they still have left in their pool. Votes have no
type, so this is a single pair of numbers, not a list.

    * @method
    * @name DigiobcanApi#meGetTargetVoteTokens
    */
  meGetTargetVoteTokens(
    targetId: string,
    parameters: {} = {},
  ): Promise<{
    active_tokens: number;
    available_tokens: number;
  }> {
    let path = "/me/vote-tokens/{targetId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{targetId}", targetId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * The signed-in citizen's vote pool as a whole, across every target.
Named `summary` and not `types` because votes have no type - the pool is
one number in, one number out.

    * @method
    * @name DigiobcanApi#meGetVoteTokensSummary
    */
  meGetVoteTokensSummary(parameters: {} = {}): Promise<{
    active_tokens: number;
    available_tokens: number;
    max_count: number;
  }> {
    let path = "/me/vote-tokens-summary";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Revoke votes for a target
   * @method
   * @name DigiobcanApi#meVoteTokenRevoke
   */
  meVoteTokenRevoke(
    parameters: {} = {},
    body: {
      target: string;
      count: number;
    },
  ): Promise<{
    revoked_count?: number;
  }> {
    let path = "/me/vote-token-revoke";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get my notifications
   * @method
   * @name DigiobcanApi#meGetNotifications
   */
  meGetNotifications(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    notifications: Array<Notification>;
    hasNextOffset: boolean;
  }> {
    let path = "/me/notifications";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Mark my notifications as read by tags

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_NOTIFICATION_TAGS

    * @method
    * @name DigiobcanApi#meNotificationsMarkAsRead
    */
  meNotificationsMarkAsRead(
    parameters: {} = {},
    body: {
      notificationTags: Array<string>;
    },
  ): Promise<{
    notifications: Array<Notification>;
    unread_count: unread_count;
  }> {
    let path = "/me/notifications/read";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get count of my unread notifications (max 10)
   * @method
   * @name DigiobcanApi#meGetNotificationsUnreadCount
   */
  meGetNotificationsUnreadCount(parameters: {} = {}): Promise<{
    unread_count: unread_count;
  }> {
    let path = "/me/notifications/unread-count";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get my notification settings
   * @method
   * @name DigiobcanApi#meGetNotificationsSettings
   */
  meGetNotificationsSettings(
    parameters: {} = {},
  ): Promise<UserNotificationSettings> {
    let path = "/me/notifications/settings";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Update user notification settings
   * @method
   * @name DigiobcanApi#meUpdateNotificationsSettings
   */
  meUpdateNotificationsSettings(
    parameters: {} = {},
    body: UserNotificationSettings,
  ): Promise<UserNotificationSettings> {
    let path = "/me/notifications/settings";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get user's web push notifications status
   * @method
   * @name DigiobcanApi#meNotificationsWebPushStatus
   */
  meNotificationsWebPushStatus(parameters: { endpoint: string }): Promise<{
    status: boolean;
  }> {
    let path = "/me/notifications/webpush";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["endpoint"] !== "undefined") {
      queryParameters["endpoint"] = parameters["endpoint"];
    }

    queryParameters["endpoint"] = this.transformParameter(
      queryParameters["endpoint"],
    );

    if (typeof parameters["endpoint"] === "undefined") {
      throw new Error("Missing required parameter: endpoint");
    }

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Subscribe user to web push notifications
   * @method
   * @name DigiobcanApi#meNotificationsWebPushSubscribe
   */
  meNotificationsWebPushSubscribe(
    parameters: {} = {},
    body: WebPushSubscription,
  ): Promise<{
    subscription: WebPushSubscription;
  }> {
    let path = "/me/notifications/webpush";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Unsubscribe user from web push notifications

Chyby, ktoré môže endpoint vrátiť:
- 404: WEBPUSH_SUBSCRIPTION_NOT_FOUND

    * @method
    * @name DigiobcanApi#meNotificationsWebPushUnsubscribe
    */
  meNotificationsWebPushUnsubscribe(parameters: { endpoint: string }): Promise<{
    endpoint: string;
  }> {
    let path = "/me/notifications/webpush";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["endpoint"] !== "undefined") {
      queryParameters["endpoint"] = parameters["endpoint"];
    }

    queryParameters["endpoint"] = this.transformParameter(
      queryParameters["endpoint"],
    );

    if (typeof parameters["endpoint"] === "undefined") {
      throw new Error("Missing required parameter: endpoint");
    }

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get user's email notifications status
   * @method
   * @name DigiobcanApi#meNotificationsEmailStatus
   */
  meNotificationsEmailStatus(parameters: {} = {}): Promise<{
    status: boolean;
  }> {
    let path = "/me/notifications/email";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   *
   * @method
   * @name DigiobcanApi#meNotificationsEmailSubscribe
   */
  meNotificationsEmailSubscribe(parameters: {} = {}): Promise<{
    email: string;
  }> {
    let path = "/me/notifications/email";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Unsubscribe user from email notifications
   * @method
   * @name DigiobcanApi#meNotificationsEmailUnsubscribe
   */
  meNotificationsEmailUnsubscribe(parameters: {} = {}): Promise<{
    message: string;
  }> {
    let path = "/me/notifications/email";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get list of visible projects
   * @method
   * @name DigiobcanApi#getProjects
   */
  getProjects(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    projects: Array<ProjectListItem>;
  }> {
    let path = "/projects";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get visible project detail
   * @method
   * @name DigiobcanApi#getProjectDetail
   */
  getProjectDetail(
    projectId: string,
    parameters: {} = {},
  ): Promise<ProjectDetail> {
    let path = "/projects/{projectId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    path = path.replace("{projectId}", projectId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get datahub news
   * @method
   * @name DigiobcanApi#getNews
   */
  getNews(
    parameters: {
      limit?: number;
      location?: string;
      category?: Array<string>;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    news: Array<News>;
    nextCursor?: Array<string>;
  }> {
    let path = "/news";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["location"] !== "undefined") {
      queryParameters["location"] = parameters["location"];
    }

    queryParameters["location"] = this.transformParameter(
      queryParameters["location"],
    );

    if (typeof parameters["category"] !== "undefined") {
      queryParameters["category"] = parameters["category"];
    }

    queryParameters["category"] = this.transformParameter(
      queryParameters["category"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Get datahub news item by ID

Chyby, ktoré môže endpoint vrátiť:
- 404: NEWS_ITEM_NOT_FOUND

    * @method
    * @name DigiobcanApi#getNewsDetail
    */
  getNewsDetail(newId: string, parameters: {} = {}): Promise<News> {
    let path = "/news/{newId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    path = path.replace("{newId}", newId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get list of existing news categories
   * @method
   * @name DigiobcanApi#getNewsCategories
   */
  getNewsCategories(
    parameters: {
      view?: "flat" | "tree";
    } = {},
  ): Promise<{
    news_categories: Array<{
      category_id: string;
      name: string;
      description?: string;
      parent_id?: string;
      sources?: Array<NewsSourcePath>;
      children?: Array<NewsCategory>;
    }>;
  }> {
    let path = "/news-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    queryParameters["view"] = "flat";

    if (typeof parameters["view"] !== "undefined") {
      queryParameters["view"] = parameters["view"];
    }

    queryParameters["view"] = this.transformParameter(queryParameters["view"]);

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Creates a new category
   * @method
   * @name DigiobcanApi#createNewsCategory
   */
  createNewsCategory(
    parameters: {} = {},
    body: NewNewsCategory,
  ): Promise<NewsCategory> {
    let path = "/news-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Deletes a category based on provided ID

Chyby, ktoré môže endpoint vrátiť:
- 404: CATEGORY_NOT_FOUND

    * @method
    * @name DigiobcanApi#deleteNewsCategory
    */
  deleteNewsCategory(categoryId: string, parameters: {} = {}): Promise<void> {
    let path = "/news-categories/{categoryId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Updates a news category
   * @method
   * @name DigiobcanApi#updateNewsCategory
   */
  updateNewsCategory(
    categoryId: string,
    parameters: {} = {},
    body: UpdateNewsCategory,
  ): Promise<NewsCategory> {
    let path = "/news-categories/{categoryId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Nahradí zdrojové kategórie, ktoré patria do kategórie, a prepočíta,
ktoré novinky do nej patria. Zdroj patrí do kategórie, ak je niektorý
jej vzor prefixom jeho cesty (`["Úradná tabuľa"]` zahŕňa aj
`["Úradná tabuľa", "Dražby"]`).

Chyby, ktoré môže endpoint vrátiť:
- 404: CATEGORY_NOT_FOUND

    * @method
    * @name DigiobcanApi#updateNewsCategorySources
    */
  updateNewsCategorySources(
    categoryId: string,
    parameters: {} = {},
    body: {
      sources: Array<NewsSourcePath>;
    },
  ): Promise<{
    sources: Array<NewsSourcePath>;
    news_count: number;
  }> {
    let path = "/news-categories/{categoryId}/sources";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Zdrojové kategórie, ktoré majú novinky, s počtom noviniek a kategóriami,
do ktorých patria. Zdroj s prázdnym `categories` je nezaradený - jeho
novinky sa nezobrazujú a neposielajú notifikácie.

    * @method
    * @name DigiobcanApi#getNewsSources
    */
  getNewsSources(parameters: {} = {}): Promise<{
    sources: Array<NewsSource>;
  }> {
    let path = "/news-sources";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Zdroje noviniek (feedy) so stavom posledného importu
   * @method
   * @name DigiobcanApi#getNewsFeeds
   */
  getNewsFeeds(parameters: {} = {}): Promise<{
    news_feeds: Array<NewsFeed>;
  }> {
    let path = "/news-feeds";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Upraví, ako sa zo zdroja skladajú novinky: šablóny kategórie, ID
a titulku a stĺpec s dátumom. Zmena sa na existujúce novinky prejaví
až synchronizáciou, preto sa zdroj po uložení hneď zaradí na import
(ako `POST /news-feeds/run` s `feed_id`).

Zmena šablóny ID dá všetkým položkám nové identifikátory - staré
novinky zdroja sa zmažú a vzniknú nové. Aby sa pritom neoznámili
znova, ďalší beh sa berie ako prvý import zdroja a nenotifikuje.

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_FEED_TEMPLATE
- 404: NEWS_FEED_NOT_FOUND
- 409: NEWS_FEED_URL_EXISTS

    * @method
    * @name DigiobcanApi#updateNewsFeed
    */
  updateNewsFeed(
    feedId: number,
    parameters: {} = {},
    body: UpdateNewsFeed,
  ): Promise<NewsFeed> {
    let path = "/news-feeds/{feedId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{feedId}", feedId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Spustí import hneď, mimo plánovača: s `feed_id` jeden zdroj (aj
vypnutý), bez neho všetky zapnuté. Import prebehne vždy celý (ako `force`), aj keď sa
obsah zdroja nezmenil. Zdroje sa prepnú do stavu `queued` a parser ich
prepíše, keď ich začne spracúvať.

Chyby, ktoré môže endpoint vrátiť:
- 404: NEWS_FEED_NOT_FOUND

    * @method
    * @name DigiobcanApi#runNewsFeeds
    */
  runNewsFeeds(
    parameters: {} = {},
    body: {
      feed_id?: number;
    },
  ): Promise<{
    queued: number;
  }> {
    let path = "/news-feeds/run";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get all public reports
   * @method
   * @name DigiobcanApi#getReports
   */
  getReports(
    parameters: {
      limit?: number;
      location?: string;
      category?: Array<string>;
      status?: Array<ReportStatus>;
      q?: string;
      orderBy?: "date" | "location" | "votes";
      orderDirection?: "asc" | "desc";
      dateFrom?: string;
      dateTo?: string;
      offset?: number;
    } = {},
  ): Promise<{
    reports: Array<{
      id: string;
      issue_id?: string;
      title: string;
      category_id?: string;
      inserted_at: string;
      processed_at?: string;
      description?: string;
      media: Array<{
        media_id?: string;
        filename: string;
        public_url: string;
        media_type: string;
        censor?: MediaCensor;
      }>;
      explicit?: boolean;
      votes_count?: number;
      location?: Location;
      status?: ReportStatus;
      distance?: number;
    }>;
    hasNextOffset: boolean;
    offset: number;
    limit: number;
  }> {
    let path = "/reports";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["location"] !== "undefined") {
      queryParameters["location"] = parameters["location"];
    }

    queryParameters["location"] = this.transformParameter(
      queryParameters["location"],
    );

    if (typeof parameters["category"] !== "undefined") {
      queryParameters["category"] = parameters["category"];
    }

    queryParameters["category"] = this.transformParameter(
      queryParameters["category"],
    );

    if (typeof parameters["status"] !== "undefined") {
      queryParameters["status"] = parameters["status"];
    }

    queryParameters["status"] = this.transformParameter(
      queryParameters["status"],
    );

    if (typeof parameters["q"] !== "undefined") {
      queryParameters["q"] = parameters["q"];
    }

    queryParameters["q"] = this.transformParameter(queryParameters["q"]);

    queryParameters["orderBy"] = "date";

    if (typeof parameters["orderBy"] !== "undefined") {
      queryParameters["orderBy"] = parameters["orderBy"];
    }

    queryParameters["orderBy"] = this.transformParameter(
      queryParameters["orderBy"],
    );

    if (typeof parameters["orderDirection"] !== "undefined") {
      queryParameters["orderDirection"] = parameters["orderDirection"];
    }

    queryParameters["orderDirection"] = this.transformParameter(
      queryParameters["orderDirection"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    if (typeof parameters["offset"] !== "undefined") {
      queryParameters["offset"] = parameters["offset"];
    }

    queryParameters["offset"] = this.transformParameter(
      queryParameters["offset"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Create a new report

Chyby, ktoré môže endpoint vrátiť:
- 400: NO_MEDIA, TOTAL_MEDIA_SIZE_TOO_LARGE, REPORT_INVALID_LOCATION, REPORT_OUTSIDE_CITY, REPORT_CATEGORY_NOT_FOUND
- 403: RECAPTCHA_VERIFICATION_FAILED, FORBIDDEN_REPORT_CATEGORY_ON_CREATE

`category_id` smie poslať len prihlásený účet s oprávnením
`pruser:reports:create:category` (inak 403
FORBIDDEN_REPORT_CATEGORY_ON_CREATE). Kontroluje sa v kóde, nie v
`security`, lebo bez kategórie je endpoint verejný. Podnet so zvolenou
kategóriou dostane nález v nej hneď a automatická kategorizácia sa preň
nespustí.

`REPORT_OUTSIDE_CITY` znamená, že poloha leží mimo územia mesta Prešov.
Ten istý polygón, podľa ktorého sa to posudzuje, vydáva `GET /geo/boundary`.

    * @method
    * @name DigiobcanApi#createReport
    */
  createReport(
    parameters: {} = {},
    form: {
      title: string;
      description: string;
      location?: string;
      media?: Array<File>;
      recaptchaToken?: string;
      category_id?: string;
    },
  ): Promise<ReportListItem> {
    let path = "/reports";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
   * Get public report detail by ID
   * @method
   * @name DigiobcanApi#getReportDetail
   */
  getReportDetail(
    reportId: string,
    parameters: {} = {},
  ): Promise<ReportDetail> {
    let path = "/reports/{reportId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get my report status
   * @method
   * @name DigiobcanApi#getReportStatus
   */
  getReportStatus(
    reportId: string,
    parameters: {} = {},
  ): Promise<{
    statuses: Array<ReportStatusItem>;
  }> {
    let path = "/reports/{reportId}/status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Returns all reports that the admin has the right to see.

Jeden prvok `reports` je jeden podnet, nie jeden nalez: nalezy
podnetu su v poli `issues` a `status` je z nich derivovany. Filtre
`category`, `status` a `overdue` vyberaju podnety, ktore maju aspon
jeden pristupny nalez zodpovedajuci filtru - `issues` v odpovedi vsak
obsahuje vsetky pristupne nalezy podnetu, aby zoznam ukazal cely
kontext, nielen tu cast, na ktoru sa filtrovalo.

**Archivovane podnety v zozname nie su**, kym si ich volajuci
nevyziada parametrom `archived` - a ten je pristupny len drzitelovi
`pruser:reports:archived:read`.

Chyby, ktore moze endpoint vratit:
- 403: REPORTS_ARCHIVED_ACCESS_FORBIDDEN

    * @method
    * @name DigiobcanApi#adminGetReports
    */
  adminGetReports(
    parameters: {
      limit?: number;
      offset?: number;
      category?: Array<string>;
      status?: Array<ReportStatus>;
      q?: string;
      orderBy?: "votes" | "date" | "location" | "deadline";
      orderDirection?: "asc" | "desc";
      dateFrom?: string;
      dateTo?: string;
      location?: string;
      overdue?: boolean;
      awaitingMyApproval?: boolean;
      assignedToMe?: boolean;
      assignee?: Array<string>;
      archived?: boolean;
    } = {},
  ): Promise<{
    reports: Array<{
      id: string;
      issue_id?: string;
      title: string;
      category_id?: string;
      inserted_at: string;
      processed_at?: string;
      description?: string;
      media: Array<{
        media_id?: string;
        filename: string;
        public_url: string;
        media_type: string;
        censor?: MediaCensor;
      }>;
      explicit?: boolean;
      votes_count?: number;
      issues?: Array<AdminReportListIssue>;
      status?: ReportStatus;
      location?: Location;
      submitted_at?: string;
      deadline_at?: string;
      awaiting_my_approval?: boolean;
      awaiting_my_revision?: boolean;
      overdue?: boolean;
      archived_at?: string;
      archived_by?: string;
      distance?: number;
    }>;
    hasNextOffset: boolean;
    offset: number;
    limit: number;
  }> {
    let path = "/admin/reports";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["offset"] !== "undefined") {
      queryParameters["offset"] = parameters["offset"];
    }

    queryParameters["offset"] = this.transformParameter(
      queryParameters["offset"],
    );

    if (typeof parameters["category"] !== "undefined") {
      queryParameters["category"] = parameters["category"];
    }

    queryParameters["category"] = this.transformParameter(
      queryParameters["category"],
    );

    if (typeof parameters["status"] !== "undefined") {
      queryParameters["status"] = parameters["status"];
    }

    queryParameters["status"] = this.transformParameter(
      queryParameters["status"],
    );

    if (typeof parameters["q"] !== "undefined") {
      queryParameters["q"] = parameters["q"];
    }

    queryParameters["q"] = this.transformParameter(queryParameters["q"]);

    if (typeof parameters["orderBy"] !== "undefined") {
      queryParameters["orderBy"] = parameters["orderBy"];
    }

    queryParameters["orderBy"] = this.transformParameter(
      queryParameters["orderBy"],
    );

    if (typeof parameters["orderDirection"] !== "undefined") {
      queryParameters["orderDirection"] = parameters["orderDirection"];
    }

    queryParameters["orderDirection"] = this.transformParameter(
      queryParameters["orderDirection"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    if (typeof parameters["location"] !== "undefined") {
      queryParameters["location"] = parameters["location"];
    }

    queryParameters["location"] = this.transformParameter(
      queryParameters["location"],
    );

    if (typeof parameters["overdue"] !== "undefined") {
      queryParameters["overdue"] = parameters["overdue"];
    }

    queryParameters["overdue"] = this.transformParameter(
      queryParameters["overdue"],
    );

    if (typeof parameters["awaitingMyApproval"] !== "undefined") {
      queryParameters["awaitingMyApproval"] = parameters["awaitingMyApproval"];
    }

    queryParameters["awaitingMyApproval"] = this.transformParameter(
      queryParameters["awaitingMyApproval"],
    );

    if (typeof parameters["assignedToMe"] !== "undefined") {
      queryParameters["assignedToMe"] = parameters["assignedToMe"];
    }

    queryParameters["assignedToMe"] = this.transformParameter(
      queryParameters["assignedToMe"],
    );

    if (typeof parameters["assignee"] !== "undefined") {
      queryParameters["assignee"] = parameters["assignee"];
    }

    queryParameters["assignee"] = this.transformParameter(
      queryParameters["assignee"],
    );

    if (typeof parameters["archived"] !== "undefined") {
      queryParameters["archived"] = parameters["archived"];
    }

    queryParameters["archived"] = this.transformParameter(
      queryParameters["archived"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Stiahnutie **celeho vyfiltrovaneho zoznamu** podnetov ako dokument -
PDF na tlac alebo CSV do Excelu.

Filtre su **tie iste ako na `GET /admin/reports`** a maju ten isty
vyznam; chyba len `limit` a `offset`. To je cely zmysel endpointu:
obrazovka je strankovana a tlac zoznamu, ktora by vydala len prave
zobrazenu stranu, by tvrdila nepravdu o rozsahu agendy. Zoznam sa
preto vydava cely, aj keby mal pat desiatok stran.

Dokument sa sklada **pri kazdom volani nanovo**, nic sa neuklada do
medzipamate, takze cisla v nom platia k okamihu stiahnutia.

Odpoved je **binarna** (`application/pdf`, alebo `text/csv` pri
`format=csv`), nie JSON, plus `Content-Disposition` s menom suboru
`podnety-<datum>.pdf` / `.csv`.

Dva rezimy:
- `simple` - prehlad tak, ako vyzera zoznam na obrazovke (tabulka).
- `extended` - navyse popis podnetu a vyjadrenia uradu ku kazdemu
  nalezu.

Rozsah je ten isty ako pri zozname: kto nema `pruser:report-categories:access-all`
ani `...:read-all`, dostane len podnety s nalezom vo svojich kategoriach,
a `archived=true` bez `pruser:reports:archived:read` je 403.

Vyber vacsi nez 5000 podnetov sa **odmietne** (`REPORTS_EXPORT_TOO_LARGE`)
namiesto tichého orezania - dokument, ktory mlcky vynecha polovicu
agendy, je horsi nez chybova hlaska. Volajuci ma zuzit filter.

Chyby, ktore moze endpoint vratit:
- 400: MISSING_LOCATION, REPORTS_EXPORT_TOO_LARGE
- 403: REPORTS_ARCHIVED_ACCESS_FORBIDDEN

    * @method
    * @name DigiobcanApi#adminExportReports
    */
  adminExportReports(
    parameters: {
      format?: "pdf" | "csv";
      mode?: "simple" | "extended";
      category?: Array<string>;
      status?: Array<ReportStatus>;
      q?: string;
      orderBy?: "votes" | "date" | "location" | "deadline";
      orderDirection?: "asc" | "desc";
      dateFrom?: string;
      dateTo?: string;
      location?: string;
      overdue?: boolean;
      awaitingMyApproval?: boolean;
      assignedToMe?: boolean;
      assignee?: Array<string>;
      archived?: boolean;
    } = {},
  ): Promise<File | string> {
    let path = "/admin/reports-export";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/pdf");
    headers.append("Accept", "text/csv");
    headers.append("Accept", "application/json");

    queryParameters["format"] = "pdf";

    if (typeof parameters["format"] !== "undefined") {
      queryParameters["format"] = parameters["format"];
    }

    queryParameters["format"] = this.transformParameter(
      queryParameters["format"],
    );

    queryParameters["mode"] = "simple";

    if (typeof parameters["mode"] !== "undefined") {
      queryParameters["mode"] = parameters["mode"];
    }

    queryParameters["mode"] = this.transformParameter(queryParameters["mode"]);

    if (typeof parameters["category"] !== "undefined") {
      queryParameters["category"] = parameters["category"];
    }

    queryParameters["category"] = this.transformParameter(
      queryParameters["category"],
    );

    if (typeof parameters["status"] !== "undefined") {
      queryParameters["status"] = parameters["status"];
    }

    queryParameters["status"] = this.transformParameter(
      queryParameters["status"],
    );

    if (typeof parameters["q"] !== "undefined") {
      queryParameters["q"] = parameters["q"];
    }

    queryParameters["q"] = this.transformParameter(queryParameters["q"]);

    if (typeof parameters["orderBy"] !== "undefined") {
      queryParameters["orderBy"] = parameters["orderBy"];
    }

    queryParameters["orderBy"] = this.transformParameter(
      queryParameters["orderBy"],
    );

    if (typeof parameters["orderDirection"] !== "undefined") {
      queryParameters["orderDirection"] = parameters["orderDirection"];
    }

    queryParameters["orderDirection"] = this.transformParameter(
      queryParameters["orderDirection"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    if (typeof parameters["location"] !== "undefined") {
      queryParameters["location"] = parameters["location"];
    }

    queryParameters["location"] = this.transformParameter(
      queryParameters["location"],
    );

    if (typeof parameters["overdue"] !== "undefined") {
      queryParameters["overdue"] = parameters["overdue"];
    }

    queryParameters["overdue"] = this.transformParameter(
      queryParameters["overdue"],
    );

    if (typeof parameters["awaitingMyApproval"] !== "undefined") {
      queryParameters["awaitingMyApproval"] = parameters["awaitingMyApproval"];
    }

    queryParameters["awaitingMyApproval"] = this.transformParameter(
      queryParameters["awaitingMyApproval"],
    );

    if (typeof parameters["assignedToMe"] !== "undefined") {
      queryParameters["assignedToMe"] = parameters["assignedToMe"];
    }

    queryParameters["assignedToMe"] = this.transformParameter(
      queryParameters["assignedToMe"],
    );

    if (typeof parameters["assignee"] !== "undefined") {
      queryParameters["assignee"] = parameters["assignee"];
    }

    queryParameters["assignee"] = this.transformParameter(
      queryParameters["assignee"],
    );

    if (typeof parameters["archived"] !== "undefined") {
      queryParameters["archived"] = parameters["archived"];
    }

    queryParameters["archived"] = this.transformParameter(
      queryParameters["archived"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Uradnici, ktorym je pridelený aspon jeden nalez, ktory volajuci vidi -
ponuka filtra `assignee` v zozname podnetov. Rozsah je ten isty ako
pri zozname: bez `pruser:report-categories:access-all` ani
`...:read-all` sa pocitaju len nalezy, ku ktorym ma volajuci pristup
(v pridelujucej kategorii obycajny clen vidi len svoje).

Kto nema pridelene nic, v ponuke nie je - filter na neho by vratil
prazdny zoznam. Presunute nalezy sa nepocitaju, archivovane podnety
ano (filter plati aj v archive). Radene podla priezviska a mena.

    * @method
    * @name DigiobcanApi#adminGetReportsAssignees
    */
  adminGetReportsAssignees(parameters: {} = {}): Promise<{
    users: Array<{
      id: string;
      name?: string;
      surname?: string;
      issues: number;
    }>;
  }> {
    let path = "/admin/reports-assignees";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Get report detail by ID

Archivovany podnet vrati `404` kazdemu, kto nema
`pruser:reports:archived:read` - nie `403`. Kod odpovede nema
prezradit, ze taky podnet existuje. S tym opravnenim sa detail vrati
vratane `archived_at` a `archived_by`.

Chyby, ktoré môže endpoint vrátiť:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_ITEM_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminGetReportDetail
    */
  adminGetReportDetail(
    reportId: string,
    parameters: {} = {},
  ): Promise<AdminReportDetail> {
    let path = "/admin/reports/{reportId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Update ReportDetail flags

Nad archivovanym podnetom sa odmieta: archivovany podnet sa neriesi,
najprv sa obnovi. Plati to aj pre volajuceho s
`pruser:reports:archived:read` - to opravnenie dava pravo citat, nie
zasahovat.

Chyby, ktore moze endpoint vratit:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_ITEM_NOT_FOUND
- 409: REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminUpdateReport
    */
  adminUpdateReport(
    reportId: string,
    parameters: {} = {},
    body: {
      explicit: boolean;
    },
  ): Promise<{
    explicit?: boolean;
  }> {
    let path = "/admin/reports/{reportId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Change status of an accessible report

`status: accepted` bez `current_issue_id` prijme otvorený nález kategórie
do riešenia a vráti `accepted`.

`status: accepted` s `current_issue_id` je **znovu prijatie** presunutého
nálezu: `current_issue_id` je otvorený nález **toho istého podnetu**, ktorý
podnet práve drží (typicky v predvolenej kategórii po triáži). Ten sa
presunie (`moved`) a v kategórii `category_id` vznikne nový nález
v stave **`open`** - do riešenia ho prijme až niekto z kategórie. Odpoveď
je preto `status: open`.

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_ISSUE_STATUS_UPDATE, ISSUE_ALREADY_ACCEPTED, EXISTING_ISSUES_IN_CATEGORIES, ISSUE_NOT_FOUND, INVALID_ACTION
- 403: FORBIDDEN_ISSUE_STATE_UPDATE
- 404: INVALID_CURRENT_ISSUE_ID (`current_issue_id` nie je otvorený nález tohto podnetu)
- 409: REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminUpdateReportStatus
    */
  adminUpdateReportStatus(
    reportId: string,
    parameters: {} = {},
    body: {
      category_id: string;
      status: "accepted" | "moved";
      new_category_ids?: Array<string>;
      current_issue_id?: string;
    },
  ): Promise<
    { status: "accepted" | "open"; issue_id: string } | { status: "moved" }
  > {
    let path = "/admin/reports/{reportId}/status";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Archivuje podnet: vyradi ho z aplikacie bez zmazania z databazy.

Archivovany podnet nevidi obcan ani jeho autor a nevstupuje do mapy,
feedu, verejneho zoznamu, notifikacii, poctov hlasov ani do hladania
duplicit. Riadky v `reports`, `issues`, `reports_media` ani
`issues_statements` sa nemazu - preto archivacia a nie DELETE.

Ma **vlastne opravnenie**, oddelene od `pruser:reports:manage`. Je to
ina trieda rozhodnutia nez kategorizacia alebo zmena stavu: podnet tym
prestava existovat pre vsetkych okrem archivu, takze to nema mat kazdy,
kto podnety spravuje. Z toho isteho dovodu to nie je dalsie pole
v `PATCH /admin/reports/{reportId}` - opravnenie je v spec-u
per-operaciu, takze ine opravnenie znamena inu operaciu.

Vratitelna: `DELETE` na tu istu cestu podnet obnovi. Kto smie
archivovat, smie aj obnovit - je to to iste rozhodnutie v druhom
smere, takze druhe opravnenie by len rozdelilo jednu pravomoc na dve
polovice, z ktorych jedna sa neda pouzit bez druhej.

Chyby, ktore moze endpoint vratit:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_ITEM_NOT_FOUND
- 409: REPORT_ALREADY_ARCHIVED

    * @method
    * @name DigiobcanApi#adminArchiveReport
    */
  adminArchiveReport(
    reportId: string,
    parameters: {} = {},
  ): Promise<ReportArchiveState> {
    let path = "/admin/reports/{reportId}/archive";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Obnovi archivovany podnet: vrati ho do aplikacie tak, ako bol pred
archivaciou. `archived_at` aj `archived_by` sa nastavia na `null`,
takze kto podnet archivoval, sa obnovenim strati - je to priznak
stavu, nie zurnal.

Obnoveny podnet je znova viditelny podla `explicit`, ktory archivacia
nemenila. Podnet oznaceny ako nevhodny sa teda obnovenim nezverejni.

Chyby, ktore moze endpoint vratit:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_ITEM_NOT_FOUND
- 409: REPORT_NOT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminRestoreReport
    */
  adminRestoreReport(
    reportId: string,
    parameters: {} = {},
  ): Promise<ReportArchiveState> {
    let path = "/admin/reports/{reportId}/archive";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get report issues by report ID
   * @method
   * @name DigiobcanApi#adminGetReportIssues
   */
  adminGetReportIssues(
    reportId: string,
    parameters: {} = {},
  ): Promise<{
    issues: Array<Issue>;
  }> {
    let path = "/admin/reports/{reportId}/issues";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get all duplicate issues for a report
   * @method
   * @name DigiobcanApi#adminGetReportDuplicates
   */
  adminGetReportDuplicates(
    reportId: string,
    parameters: {} = {},
  ): Promise<{
    duplicates: Array<Issue>;
  }> {
    let path = "/admin/reports/{reportId}/duplicates";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Odstráni podnet z kategórie nálezu - oprava omylu, keď úradník podnet
prijal (alebo nechal ležať) v kategórii, kam nepatrí (moj-presov#25).

Riadok sa **nemaže**: nález prejde do stavu `moved`, presne ako pri
presune, len bez cieľovej kategórie. Vyjadrenia na ňom zostávajú ako
história a späť ho vráti „znovu prijať nález“
(`PATCH /admin/reports/{reportId}/status` s `current_issue_id`).
Návrhy duplicít nálezu sa zmažú.

**Keď podnetu nezostane žiadny iný nález mimo `moved`** - nečaká ako
`open` v inej kategórii, nerieši sa, nie je vyriešený ani zlúčený -
dostane nový `open` nález v predvolenej kategórii (triáž). Keď má
inde aspoň `open` nález, na triáž nejde. `triage` v odpovedi hovorí,
ktorý prípad nastal.

Má **vlastné oprávnenie** `pruser:reports:issues:remove`, a k nemu
zápisový prístup ku kategórii nálezu (členstvo, v prideľujúcej
kategórii rozdeľovateľ alebo riešiteľ, alebo `access-all`). Body,
ktoré občan za prijatie nálezu dostal, zostávajú.

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_ISSUE_STATUS_UPDATE (nález nie je `open` ani `accepted`)
- 403: FORBIDDEN_ISSUE_STATE_UPDATE (bez prístupu ku kategórii nálezu)
- 409: REPORT_ARCHIVED, STATEMENT_PENDING_APPROVAL (vyjadrenie čaká
  na schválenie alebo je vrátené na úpravu), ISSUE_HAS_DUPLICATES
  (iné nálezy sú zlúčené do tohto), CATEGORY_IS_DEFAULT (nález je
  v predvolenej kategórii a podnet by sa musel vrátiť na triáž do nej)

    * @method
    * @name DigiobcanApi#adminRemoveIssue
    */
  adminRemoveIssue(
    issueId: string,
    parameters: {} = {},
  ): Promise<{
    issue_id: string;
    triage: boolean;
  }> {
    let path = "/admin/issues/{issueId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Pridelí nález konkrétnemu úradníkovi, alebo ho preradí inému.
Zrušenie pridelenia je `DELETE` na tej istej adrese. Len v kategórii,
ktorá funguje na prideľovaní (`ReportCategory.assignment`), inak 409
`CATEGORY_NOT_ASSIGNING`.

Smie to rozdeľovateľ kategórie nálezu (`can_assign` alebo `manager`)
alebo držiteľ `pruser:report-categories:access-all`, inak 403
`FORBIDDEN_ISSUE_ASSIGN`. Nález musí byť `open` alebo `accepted`
(409 `ISSUE_NOT_ASSIGNABLE`) a podnet nearchivovaný
(409 `REPORT_ARCHIVED`). Riešiteľ musí byť členom kategórie nálezu,
inak 400 `ASSIGNEE_NOT_MEMBER`.

Nový riešiteľ dostane notifikáciu, predošlý tiež.

    * @method
    * @name DigiobcanApi#adminAssignIssue
    */
  adminAssignIssue(
    issueId: string,
    parameters: {} = {},
    body: {
      user_id: string;
    },
  ): Promise<{
    issue_id: string;
    assignee?: {
      id: string;
      name?: string;
      surname?: string;
    };
    assigned_at?: string;
  }> {
    let path = "/admin/issues/{issueId}/assignee";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Zruší pridelenie nálezu - nález sa vráti rozdeľovateľom a predošlý
riešiteľ dostane notifikáciu. Tie isté podmienky ako `PUT`.

    * @method
    * @name DigiobcanApi#adminUnassignIssue
    */
  adminUnassignIssue(
    issueId: string,
    parameters: {} = {},
  ): Promise<{
    issue_id: string;
  }> {
    let path = "/admin/issues/{issueId}/assignee";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get possible duplicates by report ID grouped by category
   * @method
   * @name DigiobcanApi#adminGetIssuePossibleDuplicates
   */
  adminGetIssuePossibleDuplicates(
    issueId: string,
    parameters: {} = {},
  ): Promise<{
    possible_duplicates: Array<ReportIssuePossibleDuplicate>;
  }> {
    let path = "/admin/issues/{issueId}/possible-duplicates";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Marks issue `duplicateId` as duplicate of `issueId`.

Oba nalezy musia byt v tej istej kategorii - duplicita je vztah dvoch
podnetov v jednej kategorii, nie dvoch podnetov ako celkov.

Original (`issueId`) smie byt v **lubovolnom** stave - duplicita je
konstatovanie, ze ide o tu istu vec, a to plati aj pred prijatim.
Ked je original sam duplikatom, vztah sa **skrati**: `duplicateId` sa
zavesi rovno na koren retaze, takze v datach retaz nevznika a UI ma
vzdy jeden aktivny original.

Duplikatom (`duplicateId`) sa naopak smie stat len nalez v stave
`open` alebo `accepted`. Vyrieseny nalez uz nie - obcan dostal finalne
stanovisko a to sa mu neberie; presunuty ani zluceny nalez v kategorii
uz nezije.

Chyby, ktore moze endpoint vratit:
- 400: INVALID_DUPLICATE_ISSUE_STATUS (duplikat nie je `open` ani `accepted`)
- 400: INVALID_ACTION (original vysiel po retazi na sam duplikat)
- 400: DUPLICATE_ISSUE_CATEGORY_MISMATCH (nalezy su v roznych kategoriach)

    * @method
    * @name DigiobcanApi#adminMarkIssueDuplicate
    */
  adminMarkIssueDuplicate(
    issueId: string,
    parameters: {} = {},
    body: {
      duplicateId: string;
    },
  ): Promise<{}> {
    let path = "/admin/issues/{issueId}/duplicate";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Rozhodnutie uradnika o naleze - **jedna transakcia**.

Obrazovka detailu ma v paticke jedno tlacidlo s jednym vysvetlujucim
textom, ktory sklada dosledky vsetkych prepinacov ("Prijat a vyriesit +
prekategorizovat"). Preto je to jeden endpoint a nie tri volania: pri
ciastocnom zlyhani by nalez zostal v stave, ktory ten text slubil, ale
pouzivatel nedostal.

Prilohy vyjadrenia sa nahravaju az potom, na
`/admin/issues/{issueId}/statements/{statementId}/media` - multipart
spolu s rozhodnutim by znamenal skalarne polia a booleany ako stringy.

`new_category_ids` bez `accept` je **presun**: nalez prejde do stavu
`moved` (u uradnika nezostava) a podnet dostane nalezy v zadanych
kategoriach. S `accept` si ho uradnik necháva a kategorie sa len
pridavaju. Spat z `moved` vedie „znovu prijat nalez", teda
`PATCH /admin/reports/{reportId}/status` s `current_issue_id`.

Kombinacie, ktore endpoint odmietne:
- `duplicate_of` spolu s `accept`, `resolve` alebo `statement` - nalez,
  ktory sa stava duplikatom, preberá riesenie aj vyjadrenie originalu,
  takze vlastne stanovisko by sa nikdy nezverejnilo,
- `duplicates` s nalezom, ktory uz je `resolved`, `moved` alebo
  `duplicate` - duplikatom sa smie stat len `open` alebo `accepted`,
- prazdne telo, teda rozhodnutie, ktore nic nemeni.

Original v `duplicate_of` smie byt v lubovolnom stave; ked je sam
duplikatom, vztah sa skrati na koren retaze.

Chyby, ktore moze endpoint vratit:
- 400: INVALID_ACTION, INVALID_ISSUE_STATUS_UPDATE, ISSUE_NOT_FOUND,
  EXISTING_ISSUES_IN_CATEGORIES, INVALID_DUPLICATE_ISSUE_STATUS,
  DUPLICATE_ISSUE_CATEGORY_MISMATCH

Kategoria, v ktorej podnet nalez uz ma (`open`, `accepted`,
`resolved`, `duplicate`), **nie je chyba** - `new_category_ids` sa o ne
tiho ochudobni. Vytvarat netreba nic, co uz existuje.

Kategoria, z ktorej nalez uz raz **presunuli** (`moved`), naopak vrati
`EXISTING_ISSUES_IN_CATEGORIES` - je to ochrana pred slučkou. Nazad
vedie len „znovu prijat nalez": uradnik, ktory nalez drzi, nadiktuje
jeho identifikator kolegovi zodpovednemu za tu kategoriu a ten ho zada
v svojom presunutom naleze.
- 403: FORBIDDEN_ISSUE_STATE_UPDATE
- 409: STATEMENT_PENDING_APPROVAL, REPORT_ARCHIVED

**Schvalovanie vyjadreni.** Ked ma uradnik v kategorii naleza
`requires_approval`, jeho vyjadrenie sa ulozi ako cakajuce
(`approval: pending`): obcan ho nevidi a nedostane o nom notifikaciu,
kym ho schvalovatel nepusti (`POST /admin/statements/{statementId}/approval`).
`resolve` sa vtedy **odklada** - nalez zostava `accepted`, lehota bezi
dalej a vyriesi sa az schvalenim finalneho vyjadrenia. Preto
`resolve` bez `statement` taky uradnik poslat nemoze (400
`STATEMENT_REQUIRED_FOR_APPROVAL`): nebolo by co schvalit.
Kym je na naleze vyjadrenie v schvalovani - caka (`pending`) alebo
je vratene autorovi na upravu (`rejected`) - dalsie ani `resolve`
neprejde (409 `STATEMENT_PENDING_APPROVAL`); presun a duplicita ano.

    * @method
    * @name DigiobcanApi#adminIssueDecision
    */
  adminIssueDecision(
    issueId: string,
    parameters: {} = {},
    body: {
      accept?: boolean;
      resolve?: boolean;
      new_category_ids?: Array<string>;
      duplicate_of?: string;
      duplicates?: Array<string>;
      dismiss_duplicates?: boolean;
      statement?: {
        text: string;
      };
    },
  ): Promise<{
    status: ReportStatus;
    statement_id?: string;
    approval?: StatementApproval;
  }> {
    let path = "/admin/issues/{issueId}/decision";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Vyjadrenia k nalezu, od najnovsieho.
   * @method
   * @name DigiobcanApi#adminGetIssueStatements
   */
  adminGetIssueStatements(
    issueId: string,
    parameters: {} = {},
  ): Promise<{
    statements: Array<IssueStatement>;
  }> {
    let path = "/admin/issues/{issueId}/statements";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Rozhodnutie o cakajucom vyjadreni (`approval: pending`) - schvalit
alebo vratit autorovi.

Smie ho urobit uradnik, ktory ma v kategorii naleza `can_approve`
alebo `manager` - aj vlastne vyjadrenie, rozhoduje priznak, nie autorstvo.
`pruser:report-categories:access-all` k tomu **nestaci** - schvalovanie
je priznak clenstva, nie celomestsky rozsah. A naopak: `security` chce
len `pruser:reports:read`, nie `…:manage`. Vedúci, ktorý sám nálezy
nerieši, schvaľovať smie, keď mu to v kategórii zapnú - inak by
priznak `can_approve` ticho nič neznamenal.

- `approve` vyjadrenie zverejni. S `text` ho pred zverejnenim upravi;
  znenie autora zostane v historii kol (`reviews[].submitted_text`). Priebezne
  vyjadrenie odide obcanovi ako notifikacia, finalne nalez **vyriesi**
  (`resolved`, lehota konci tymto okamihom) a obcan dostane spravu
  o vyrieseni s jeho textom.
- `reject` vyjadrenie vrati autorovi **na upravu**. Obcan sa o nom
  nedozvie, kym ho niekto neschvali. Autor ho upravi a posle znova
  (`POST /admin/statements/{statementId}/resubmit`), alebo ho stiahne
  (`…/withdraw`); dovtedy blokuje dalsie vyjadrenia k nalezu.

`note` je interna poznamka pre autora pri oboch rozhodnutiach; obcan ju
nevidi. Kazde rozhodnutie je riadok v historii kol (`reviews`).
Autor dostane o rozhodnuti notifikaciu.

Chyby, ktore moze endpoint vratit:
- 400: INVALID_ACTION (`text` pri `reject`)
- 403: FORBIDDEN_STATEMENT_APPROVAL
- 404: STATEMENT_NOT_FOUND
- 409: STATEMENT_NOT_PENDING, INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminReviewStatement
    */
  adminReviewStatement(
    statementId: string,
    parameters: {} = {},
    body: {
      decision: "approve" | "reject";
      text?: string;
      note?: string;
    },
  ): Promise<{
    status: ReportStatus;
    approval: StatementApproval;
  }> {
    let path = "/admin/statements/{statementId}/approval";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Autor posle vratene vyjadrenie (`approval: rejected`) znova s
upravenym textom. Predosle kolo zostava v historii (`reviews`). Druh
vyjadrenia (priebezne / finalne) sa nemeni.

Rozhoduje **aktualny** priznak `requires_approval` autora v kategorii
naleza, nie ten z case vratenia:
- stale ho ma (alebo uz nie je clenom kategorie): vyjadrenie sa vrati
  do `pending` a schvalovatelia dostanu notifikaciu ako pri novom,
- medzitym mu ho vypli: vyjadrenie sa zverejni hned (`approval` NULL,
  `published: true`) - priebezne posle obcanovi spravu, finalne nalez
  vyriesi (`status: resolved`).

Chyby, ktore moze endpoint vratit:
- 403: FORBIDDEN_STATEMENT_RESUBMIT (len autor)
- 404: STATEMENT_NOT_FOUND
- 409: STATEMENT_NOT_REJECTED, INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminResubmitStatement
    */
  adminResubmitStatement(
    statementId: string,
    parameters: {} = {},
    body: {
      text: string;
    },
  ): Promise<{
    published: boolean;
    approval?: StatementApproval;
    status: ReportStatus;
  }> {
    let path = "/admin/statements/{statementId}/resubmit";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Stiahne vratene vyjadrenie (`approval: rejected` -> `withdrawn`), aby
prestalo blokovat nalez. Obcan ho nevidi nikdy, riadok aj historia kol
zostavaju. Smie autor, alebo schvalovatel ci spravca kategorie - aby
vratene vyjadrenie odisleho kolegu nezablokovalo nalez navzdy.

Chyby, ktore moze endpoint vratit:
- 403: FORBIDDEN_STATEMENT_WITHDRAW
- 404: STATEMENT_NOT_FOUND
- 409: STATEMENT_NOT_REJECTED, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminWithdrawStatement
    */
  adminWithdrawStatement(
    statementId: string,
    parameters: {} = {},
  ): Promise<{
    approval: StatementApproval;
  }> {
    let path = "/admin/statements/{statementId}/withdraw";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Prida prilohy k vyjadreniu. Vola sa hned po `decision`, ked ma
vyjadrenie subory.

Chyby: 404 STATEMENT_NOT_FOUND, 403 FORBIDDEN_ISSUE_STATE_UPDATE.

    * @method
    * @name DigiobcanApi#adminAddIssueStatementMedia
    */
  adminAddIssueStatementMedia(
    issueId: string,
    statementId: string,
    parameters: {} = {},
    form: {
      media?: Array<File>;
    },
  ): Promise<{
    media: Array<IssueStatementMedia>;
  }> {
    let path = "/admin/issues/{issueId}/statements/{statementId}/media";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    path = path.replace("{issueId}", issueId.toString());

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
    * Zmaze prilohu vyjadrenia - riadok aj subor.

Len prilohu, ktoru obcan este nevidel: vyjadrenia v schvalovani
(`pending` / `rejected`) alebo upravy, ktora este nie je zverejnena.
Zverejnena priloha je sucastou historie upravy a odobera sa upravou
(`removed_media_ids` v `POST /admin/statements/{statementId}/revisions`).

Chyby: 404 STATEMENT_MEDIA_NOT_FOUND, 403 FORBIDDEN_ISSUE_STATE_UPDATE,
409 STATEMENT_MEDIA_PUBLISHED.

    * @method
    * @name DigiobcanApi#adminDeleteIssueStatementMedia
    */
  adminDeleteIssueStatementMedia(
    statementId: string,
    mediaId: string,
    parameters: {} = {},
  ): Promise<void> {
    let path = "/admin/statements/{statementId}/media/{mediaId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Odstrani zverejnene vyjadrenie. Vyjadrenie sa nemaze - dostane
`deleted_at` a `deleted_by`, obcan ho prestane vidiet a uradnik ho
v historii vidi preciarknute aj s tym, kto a kedy ho odstranil.

Smie kazdy, kto smie riesit kategoriu naleza (ako pri uprave).
Odstranit sa da aj finalne vyjadrenie - vyrieseny nalez sa tym vrati
do riesenia (`accepted`), lebo bez finalneho stanoviska vyrieseny nie
je. Obcan spravu nedostane. Vyjadrenie v schvalovani sa neodstranuje,
to sa stiahne (`…/withdraw`); vyjadrenie s upravou v schvalovani tiez
nie.

Chyby, ktore moze endpoint vratit:
- 403: FORBIDDEN_ISSUE_STATE_UPDATE
- 404: STATEMENT_NOT_FOUND
- 409: STATEMENT_NOT_PUBLISHED, STATEMENT_DELETED,
  STATEMENT_REVISION_PENDING, INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminDeleteStatement
    */
  adminDeleteStatement(
    statementId: string,
    parameters: {} = {},
  ): Promise<{
    deleted_at: string;
  }> {
    let path = "/admin/statements/{statementId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Uprava zverejneneho vyjadrenia (moj-presov#18) - text aj prilohy.
Obcan vidi len najnovsie znenie so stitkom „upravene" (`edited_at`),
uradnik celu historiu (`IssueStatement.revisions`).

Smie kazdy, kto smie riesit kategoriu naleza (rovnaky pristup ako
pri rozhodnuti), nielen autor vyjadrenia. Nalez musi byt `open`,
`accepted` alebo `resolved` - finalne vyjadrenie sa upravuje aj po
vyrieseni a nalez tym nemeni stav.

Ked ma upravujuci v kategorii `requires_approval`, uprava caka na
schvalenie (`approval: pending`) a obcan dovtedy vidi doterajsie
znenie. Schvaluje sa `POST /admin/statement-revisions/{revisionId}/approval`.
Inak sa uprava zverejni hned a obcan dostane notifikaciu.

Nove prilohy sa nahravaju az potom, na
`POST /admin/statement-revisions/{revisionId}/media` - rovnako ako
pri novom vyjadreni.

Rozpracovana uprava (`pending` / `rejected`) smie byt na vyjadreni
najviac jedna. Nalez neblokuje - dalsie vyjadrenie aj vyriesenie idu.

Chyby, ktore moze endpoint vratit:
- 400: STATEMENT_MEDIA_NOT_FOUND (`removed_media_ids` mimo aktualnych priloh)
- 403: FORBIDDEN_ISSUE_STATE_UPDATE
- 404: STATEMENT_NOT_FOUND
- 409: STATEMENT_NOT_PUBLISHED, STATEMENT_REVISION_PENDING,
  INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminReviseStatement
    */
  adminReviseStatement(
    statementId: string,
    parameters: {} = {},
    body: StatementRevisionInput,
  ): Promise<StatementRevisionResult> {
    let path = "/admin/statements/{statementId}/revisions";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{statementId}", statementId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Prida prilohy k uprave vyjadrenia. Vola sa hned po
`POST /admin/statements/{statementId}/revisions`, ked uprava pridava
subory, alebo pred opatovnym odoslanim vratenej upravy. Kym uprava nie
je zverejnena, obcan ich nevidi.

Smie len autor upravy a len k neziahnutej uprave.

Chyby: 404 STATEMENT_REVISION_NOT_FOUND, 403 FORBIDDEN_STATEMENT_REVISION,
409 STATEMENT_REVISION_WITHDRAWN, REPORT_ARCHIVED.

    * @method
    * @name DigiobcanApi#adminAddStatementRevisionMedia
    */
  adminAddStatementRevisionMedia(
    revisionId: string,
    parameters: {} = {},
    form: {
      media?: Array<File>;
    },
  ): Promise<{
    media: Array<IssueStatementMedia>;
  }> {
    let path = "/admin/statement-revisions/{revisionId}/media";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    path = path.replace("{revisionId}", revisionId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
    * Rozhodnutie o cakajucej uprave vyjadrenia (`approval: pending`).
Rovnake pravidla ako `POST /admin/statements/{statementId}/approval`:
smie `can_approve` alebo `manager` v kategorii naleza, `access-all`
nestaci.

- `approve` upravu zverejni - text aj prilohy sa prejavia na
  vyjadreni a obcan dostane notifikaciu. S `text` ju schvalovatel pred
  zverejnenim upravi; znenie autora zostane v `reviews[].submitted_text`.
  Stav naleza sa nemeni ani pri finalnom vyjadreni.
- `reject` upravu vrati autorovi. Obcan dalej vidi doterajsie znenie.

Chyby, ktore moze endpoint vratit:
- 400: INVALID_ACTION (`text` pri `reject`)
- 403: FORBIDDEN_STATEMENT_APPROVAL
- 404: STATEMENT_REVISION_NOT_FOUND
- 409: STATEMENT_NOT_PENDING, INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminReviewStatementRevision
    */
  adminReviewStatementRevision(
    revisionId: string,
    parameters: {} = {},
    body: {
      decision: "approve" | "reject";
      text?: string;
      note?: string;
    },
  ): Promise<{
    approval: StatementApproval;
  }> {
    let path = "/admin/statement-revisions/{revisionId}/approval";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{revisionId}", revisionId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Autor posle vratenu upravu (`approval: rejected`) znova. Ako pri
vyjadreni rozhoduje **aktualny** priznak `requires_approval` autora:
stale ho ma - uprava sa vrati do `pending`; medzitym mu ho vypli -
zverejni sa hned (`published: true`).

Chyby, ktore moze endpoint vratit:
- 400: STATEMENT_MEDIA_NOT_FOUND
- 403: FORBIDDEN_STATEMENT_RESUBMIT (len autor)
- 404: STATEMENT_REVISION_NOT_FOUND
- 409: STATEMENT_NOT_REJECTED, INVALID_ISSUE_STATUS_UPDATE, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminResubmitStatementRevision
    */
  adminResubmitStatementRevision(
    revisionId: string,
    parameters: {} = {},
    body: StatementRevisionInput,
  ): Promise<StatementRevisionResult> {
    let path = "/admin/statement-revisions/{revisionId}/resubmit";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{revisionId}", revisionId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Stiahne vratenu upravu (`rejected` -> `withdrawn`), aby sa vyjadrenie
dalo upravit znova. Obcan ju nevidi nikdy. Smie autor, schvalovatel
alebo spravca kategorie.

Chyby, ktore moze endpoint vratit:
- 403: FORBIDDEN_STATEMENT_WITHDRAW
- 404: STATEMENT_REVISION_NOT_FOUND
- 409: STATEMENT_NOT_REJECTED, REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminWithdrawStatementRevision
    */
  adminWithdrawStatementRevision(
    revisionId: string,
    parameters: {} = {},
  ): Promise<{
    approval: StatementApproval;
  }> {
    let path = "/admin/statement-revisions/{revisionId}/withdraw";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{revisionId}", revisionId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Prida prilohy k obalke podnetu. Subory prejdu tym istym spracovanim
ako pri zalozeni podnetu (HEIC -> JPEG, varianty, EXIF poloha)
a publikuje sa MQTT sprava, aby im reports-processor dopocital
embedding - bez neho by do detekcie duplicit nevstupili.

Chyby: 404 REPORTS_ITEM_NOT_FOUND, 403 REPORTS_ACCESS_FORBIDDEN.

    * @method
    * @name DigiobcanApi#adminAddReportMedia
    */
  adminAddReportMedia(
    reportId: string,
    parameters: {} = {},
    form: {
      media?: Array<File>;
    },
  ): Promise<{
    media: Array<{
      media_id: string;
      public_url: string;
      original_filename?: string;
      media_type?: string;
    }>;
  }> {
    let path = "/admin/reports/{reportId}/media";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
    * Zmaze prilohu obalky - riadok aj subor.

Mazanie prilohy meni dokazy ohlasovatela, preto sa zaznamenava do logu
(`adminDeleteReportMedia-deleted` s `clientAccId` a `media_id`).

Chyby: 404 REPORTS_MEDIA_NOT_FOUND, 403 REPORTS_ACCESS_FORBIDDEN.

    * @method
    * @name DigiobcanApi#adminDeleteReportMedia
    */
  adminDeleteReportMedia(
    reportId: string,
    mediaId: string,
    parameters: {} = {},
  ): Promise<void> {
    let path = "/admin/reports/{reportId}/media/{mediaId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Cenzura fotky v prilohe - mozaika cez oznacene bunky mriezky.

Original sa **neprepisuje**. Pri prvej cenzure sa presunie do
adresara, ktory nginx priloh nepodava (`media.originalsDir`), a verejne
sa odvtedy podava cenzurovana kopia s **novym menom suboru** - stara
adresa originalu mohla uniknut (feed, zdielanie), takze musi prestat
fungovat. Kazda dalsia uprava sklada kopiu nanovo z originalu, takze
guma funguje a maska sa neprida k uz rozmazanej fotke.

`public_url` v odpovedi je preto ina nez pred volanim.

Cenzurovat sa da len fotka (JPEG, PNG, WebP, AVIF, TIFF). Mazanie
cenzury je `DELETE` na tej istej adrese, nie prazdna maska.

Chyby:
- 400: REPORTS_MEDIA_NOT_CENSORABLE, REPORTS_MEDIA_CENSOR_INVALID
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_MEDIA_NOT_FOUND
- 409: REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminCensorReportMedia
    */
  adminCensorReportMedia(
    reportId: string,
    mediaId: string,
    parameters: {} = {},
    body: MediaCensor,
  ): Promise<AdminReportMedia> {
    let path = "/admin/reports/{reportId}/media/{mediaId}/censor";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Odstrani cenzuru - original sa vrati medzi verejne subory (opat pod
novym menom) a cenzurovana kopia sa zmaze.

Na necenzurovanej prilohe nerobi nic a vrati ju tak, ako je.

Chyby:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_MEDIA_NOT_FOUND
- 409: REPORT_ARCHIVED

    * @method
    * @name DigiobcanApi#adminUncensorReportMedia
    */
  adminUncensorReportMedia(
    reportId: string,
    mediaId: string,
    parameters: {} = {},
  ): Promise<AdminReportMedia> {
    let path = "/admin/reports/{reportId}/media/{mediaId}/censor";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Original prilohy - pri cenzurovanej fotke ten, ktory verejne podavany
nie je. Cita ho editor cenzury, aby masku kreslil nad originalom.

Pri necenzurovanej prilohe je to ten isty subor ako `public_url`.
Odpoved je **binarna** s `Content-Type` prilohy.

Chyby:
- 403: REPORTS_ACCESS_FORBIDDEN
- 404: REPORTS_MEDIA_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminGetReportMediaOriginal
    */
  adminGetReportMediaOriginal(
    reportId: string,
    mediaId: string,
    parameters: {} = {},
  ): Promise<File> {
    let path = "/admin/reports/{reportId}/media/{mediaId}/original";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/octet-stream");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Get a list of existing report categories.

**Vyzaduje prihlasenie, ziadne konkretne opravnenie.** Endpoint bol do
verzie 5.0.0 verejny, a to len historicky - nic neprihlasene ho necita.
Formular na nahlasenie podnetu kategoriu nevybera vobec, priraduje ju
`reports-processor`; vsetci volajuci su v `/administracia`.

Preco nie rovno `pruser:report-categories:manage`: detail podnetu je za
`pruser:reports:read` / `:manage` a potrebuje zoznam kategorii na
prekategorizovanie nalezu, takze uradnik bez prava na spravu kategorii
by ostal bez ponuky. Menny zoznam zodpovednych uradnikov (`users`)
a hladanie podla ich mien su preto gatovane zvlast.

    * @method
    * @name DigiobcanApi#getReportCategories
    */
  getReportCategories(
    parameters: {
      view?: "flat" | "tree";
      q?: string;
    } = {},
  ): Promise<{
    report_categories: Array<{
      category_id: string;
      name: string;
      description?: string;
      parent_id?: string;
      users_count?: number;
      full_path?: string;
      users?: Array<{
        id: string;
        name?: string;
        surname?: string;
      }>;
      is_default: boolean;
      assignment?: boolean;
      children?: Array<{
        category_id: string;
        name: string;
        description?: string;
        parent_id?: string;
        users_count?: number;
        full_path?: string;
        users?: Array<{
          id: string;
          name?: string;
          surname?: string;
        }>;
        is_default: boolean;
        assignment?: boolean;
      }>;
    }>;
  }> {
    let path = "/reports-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["view"] = "flat";

    if (typeof parameters["view"] !== "undefined") {
      queryParameters["view"] = parameters["view"];
    }

    queryParameters["view"] = this.transformParameter(queryParameters["view"]);

    if (typeof parameters["q"] !== "undefined") {
      queryParameters["q"] = parameters["q"];
    }

    queryParameters["q"] = this.transformParameter(queryParameters["q"]);

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Creates a new report category
   * @method
   * @name DigiobcanApi#createReportCategory
   */
  createReportCategory(
    parameters: {} = {},
    body: NewReportCategory,
  ): Promise<ReportCategory> {
    let path = "/reports-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
   * Get report category by ID
   * @method
   * @name DigiobcanApi#getReportCategoryDetail
   */
  getReportCategoryDetail(
    categoryId: string,
    parameters: {} = {},
  ): Promise<ReportCategory> {
    let path = "/reports-categories/{categoryId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * **Zrusi** kategoriu aj s jej podkategoriami - nezmaze ich. Zrusena
kategoria zmizne z ponuk, zo spravy kategorii aj z taxonomie AI
(`deleted_at`, migracia 000035), ale uzavrete nalezy v nej
(`resolved`, `duplicate`, `moved`) zostanu aj s vyjadreniami - su to
historia podnetu a cudzi kluc ich zmazat nedovoli.

**Zive nalezy** (`open`, `accepted`) sa presunu do kategorie `moveTo`
(bez nej do predvolenej): povodny sa oznaci ako presunuty a v cieli
vznikne **novy podany** (`open`), aj ked z cielovej kategorie podnet
raz odisiel - ochrana pred sluckou (`EXISTING_ISSUES_IN_CATEGORIES`)
tu neplati, kategoria, z ktorej nalez ide, uz nebude. Prijaty sa
nalez v cieli neprijima, posudi ho jeho kategoria nanovo. Ked podnet
v cieli zivy alebo uzavrety nalez uz ma, novy nevznika.

Opakovane volanie nad zrusenou kategoriou nic nerobi (`moved: 0`).

Chyby, ktore moze endpoint vratit:
- 400: INVALID_MOVE_TARGET (`moveTo` neexistuje, je zrusena alebo
  lezi v rusenom podstrome)
- 404: CATEGORY_NOT_FOUND
- 409: CATEGORY_IS_DEFAULT (predvolena je v rusenom podstrome),
  CATEGORY_RETIRE_CONFLICT (podnet ma v rusenej kategorii zivy aj
  presunuty nalez a v cieli uz nalez ma - treba ho vyriesit rucne)

    * @method
    * @name DigiobcanApi#deleteReportCategory
    */
  deleteReportCategory(
    categoryId: string,
    parameters: {
      moveTo?: string;
    } = {},
  ): Promise<{
    categories: number;
    moved: number;
  }> {
    let path = "/reports-categories/{categoryId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    if (typeof parameters["moveTo"] !== "undefined") {
      queryParameters["moveTo"] = parameters["moveTo"];
    }

    queryParameters["moveTo"] = this.transformParameter(
      queryParameters["moveTo"],
    );

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Updates a report category
   * @method
   * @name DigiobcanApi#updateReportCategory
   */
  updateReportCategory(
    categoryId: string,
    parameters: {} = {},
    body: UpdateReportCategory,
  ): Promise<ReportCategory> {
    let path = "/reports-categories/{categoryId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Prehlasi kategoriu za predvolenu - tu, do ktorej padne podnet, ktory sa
nepodarilo zaradit.

Priznak drzi **najviac jedna** kategoria (partial unique index
`report_categories_one_default_idx`), takze tento endpoint ho tej
doterajsej v tej istej transakcii odoberie. Preto nema telo: nie je co
nastavovat, iba komu. Odobrat priznak bez toho, aby ho dostala ina
kategoria, sa cez API neda - stav bez predvolenej kategorie vznika len
jej zmazanim a `deleteReportCategory` ho prave preto odmieta.

Vracia kategoriu po zmene. Ktora kategoria priznak stratila, hovori
`previous`; je `null`, ked ziadna predvolena nebola alebo ked uz
predvolena bola prave tato.

    * @method
    * @name DigiobcanApi#setDefaultReportCategory
    */
  setDefaultReportCategory(
    categoryId: string,
    parameters: {} = {},
  ): Promise<{
    category: ReportCategory;
    previous?: {
      category_id: string;
      name: string;
    };
  }> {
    let path = "/reports-categories/{categoryId}/default";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Clenovia kategorie podnetov aj s priznakmi clenstva.

Smie to drzitel `pruser:report-categories:users:manage` (vsetky
kategorie), **spravca tejto kategorie** (`manager` na jeho
clenstve) alebo jej **rozdelovatel** (`can_assign`) - ten len cita,
aby mal z koho vyberat riesitela. Kontroluje sa v kode, nie
v `security` - spravcovi netreba celomestske opravnenie. Inak 403
`FORBIDDEN_CATEGORY_USERS_MANAGE`.

    * @method
    * @name DigiobcanApi#getReportCategoryUsers
    */
  getReportCategoryUsers(
    categoryId: string,
    parameters: {} = {},
  ): Promise<{
    users?: Array<CategoryUser>;
  }> {
    let path = "/reports-categories/{categoryId}/users";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Priradí kategóriu zadaným používateľom.

Každá položka nesie **presne jeden** identifikátor, presne ako ho vrátilo
`GET /users`: `client_acc_id` pre človeka, ktorý sa do aplikácie už
prihlásil, alebo `acc_id` pre toho, kto ešte nie - tomu sa identifikátor
v aplikácii vytvorí (`POST /app/accounts/aliases` v datahub-api). Ani
jeden alebo oba naraz znamenajú 400 `MISSING_ACCOUNT_ID`.

Nový člen má všetky príznaky vypnuté (nie je správca, neschvaľuje,
nepotrebuje schválenie); nastavujú sa cez `PATCH` na člena.

Smie to drzitel `pruser:report-categories:users:manage` (vsetky
kategorie) alebo **spravca tejto kategorie** (`manager` na jeho
clenstve). Kontroluje sa v kode, nie v `security` - spravcovi netreba
celomestske opravnenie. Inak 403 `FORBIDDEN_CATEGORY_USERS_MANAGE`.

Chyby, ktoré môže endpoint vrátiť:
- 400: MISSING_ACCOUNT_ID
- 403: FORBIDDEN_CATEGORY_USERS_MANAGE

    * @method
    * @name DigiobcanApi#addReportCategoryUsers
    */
  addReportCategoryUsers(
    categoryId: string,
    parameters: {} = {},
    body: Array<{
      client_acc_id?: string;
      acc_id?: string;
      name?: string;
      surname?: string;
    }>,
  ): Promise<{
    category_id: string;
    name: string;
    users: Array<UserDetail>;
  }> {
    let path = "/reports-categories/{categoryId}/users";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Zmeni priznaky clenstva v kategorii. Neuvedeny priznak sa nemeni.

`requires_approval` sa vylucuje s `can_approve` aj s `manager` - kto
potrebuje, aby mu vyjadrenie niekto schvalil, nemoze ho sam schvalovat
inym, a spravca schvalovat smie. Taka kombinacia (aj ked vznikne
spojenim s doterajsim stavom) je 400 `INVALID_CATEGORY_USER_FLAGS`.

Smie to drzitel `pruser:report-categories:users:manage` (vsetky
kategorie) alebo **spravca tejto kategorie** (`manager` na jeho
clenstve). Kontroluje sa v kode, nie v `security` - spravcovi netreba
celomestske opravnenie. Inak 403 `FORBIDDEN_CATEGORY_USERS_MANAGE`.

Chyby, ktore moze endpoint vratit:
- 400: INVALID_CATEGORY_USER_FLAGS, INVALID_ACTION
- 403: FORBIDDEN_CATEGORY_USERS_MANAGE, FORBIDDEN_OWN_MANAGER_REMOVAL
  (spravca si sam vypina `manager`; smie to len s celomestskym
  `pruser:report-categories:users:manage`)
- 404: USER_NOT_RESPONSIBLE

    * @method
    * @name DigiobcanApi#updateReportCategoryUser
    */
  updateReportCategoryUser(
    categoryId: string,
    userId: string,
    parameters: {} = {},
    body: {
      manager?: boolean;
      can_approve?: boolean;
      requires_approval?: boolean;
      can_assign?: boolean;
    },
  ): Promise<CategoryUser> {
    let path = "/reports-categories/{categoryId}/users/{userId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    path = path.replace("{userId}", userId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Odoberie clena z kategorie.

Smie to drzitel `pruser:report-categories:users:manage` (vsetky
kategorie) alebo **spravca tejto kategorie** (`manager` na jeho
clenstve). Kontroluje sa v kode, nie v `security` - spravcovi netreba
celomestske opravnenie. Inak 403 `FORBIDDEN_CATEGORY_USERS_MANAGE`.

Spravca sa nesmie odobrat sam (403 `FORBIDDEN_OWN_MANAGER_REMOVAL`) -
kategoria by zostala bez spravcu. Drzitel `users:manage` smie.

    * @method
    * @name DigiobcanApi#deleteReportCategoryUser
    */
  deleteReportCategoryUser(
    categoryId: string,
    userId: string,
    parameters: {} = {},
  ): Promise<{
    category_id: string;
    name: string;
  }> {
    let path = "/reports-categories/{categoryId}/users/{userId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{categoryId}", categoryId.toString());

    path = path.replace("{userId}", userId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Vyhľadá používateľov, ktorých má zmysel priradiť ku kategórii podnetov,
teda držiteľov úradníckych oprávnení (`pruser:reports:read`,
`pruser:reports:manage`, `pruser:report-categories:access-all`,
`pruser:report-categories:read-all`).
Zoznam oprávnení je fixný v kóde - z requestu sa rozšíriť nedá.

Zoznam účtov drží datahub-api; táto služba sa doň pýta aplikačným
tokenom, takže prehliadač úradníka žiadny token k účtom platformy
nedrží. Vracia len identifikátor, meno a priezvisko.

Používateľ, ktorý sa do Môjho Prešova ešte neprihlásil, tu má namiesto
`client_acc_id` len `acc_id` - priradenie kategórie mu identifikátor
v aplikácii dorobí samo.

Smie to držiteľ `pruser:report-categories:users:manage` alebo správca
aspoň jednej kategórie - ten si hľadá, koho pridať do svojej. Kontroluje
sa v kóde, inak 403 `FORBIDDEN_CATEGORY_USERS_MANAGE`.

    * @method
    * @name DigiobcanApi#searchUsers
    */
  searchUsers(
    parameters: {
      q?: string;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{
    users: Array<UserCandidate>;
  }> {
    let path = "/users";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["q"] !== "undefined") {
      queryParameters["q"] = parameters["q"];
    }

    queryParameters["q"] = this.transformParameter(queryParameters["q"]);

    queryParameters["limit"] = 20;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["offset"] !== "undefined") {
      queryParameters["offset"] = parameters["offset"];
    }

    queryParameters["offset"] = this.transformParameter(
      queryParameters["offset"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get a list of categories assigned on user
   * @method
   * @name DigiobcanApi#getUserReportCategories
   */
  getUserReportCategories(
    userId: string,
    parameters: {} = {},
  ): Promise<{
    categories?: Array<{
      name: string;
      category_id: string;
    }>;
  }> {
    let path = "/users/{userId}/reports-categories";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{userId}", userId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Get list of public votings, sorted by open_from and voting_id, paginated by limit and pagecursor.

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_PAGE_CURSOR

    * @method
    * @name DigiobcanApi#getVotings
    */
  getVotings(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    votings: Array<VotingListItem>;
  }> {
    let path = "/votings";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Get public voting detail by ID
   * @method
   * @name DigiobcanApi#getVotingDetail
   */
  getVotingDetail(
    votingId: string,
    parameters: {} = {},
  ): Promise<VotingDetail> {
    let path = "/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Get results for a voting.

Prázdne `questions` nie sú chyba, ale odpoveď „podrobnosti sa teraz nevydávajú": buď hlasovanie ešte beží a nemá zapnuté `available_incomplete_results`, alebo má `available_results: false` a nezverejní ich nikdy. Správca hlasovaní (`pruser:votings:manage`) dostane podrobnosti vždy - obe obmedzenia platia len pre občana.
    * @method
    * @name DigiobcanApi#getVotingResults
    */
  getVotingResults(
    votingId: string,
    parameters: {} = {},
  ): Promise<{
    voting_id: string;
    title: string;
    description?: string;
    open_from?: string;
    open_to?: string;
    votes_count: number;
    verified_accounts_only: boolean;
    questions: Array<VotingQuestionWithResults>;
  }> {
    let path = "/votings/{votingId}/results";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Odovzdanie hlasu v hlasovaní.

`votings_results` nemá stĺpec s účtom. Riadok odpovede prihláseného
nesie `voter_key`, odtlačok odvodený z účtu kľúčom, ktorý **nie je
v databáze** (`hmac(masterKey, client_acc_id)` ďalej cez `voting_id`,
`question_id` a `voting.secret`) - kto získa databázu, hlas k osobe
nepriradí, ale služba ho hlasujúcemu vie zobraziť
(`VotingDetail.my_votes`). Je to teda anonymita voči údajom v databáze,
nie voči prevádzkovateľovi platformy, a tak sa to má aj opisovať.

Prihlásenie navyše slúži na deduplikáciu (`votings_participation` drží
`hash(client_acc_id, voting_id, secret)`) a na gamifikáciu.

**Anonymný hlas `voter_key` nedostane** - nie je z čoho ho odvodiť -
takže si ho hlasujúci ani nepozrie.

Kto smie hlasovať, určuje samotné hlasovanie:
- `allow_anonymous: true` - smie ktokoľvek; neprihlásený musí poslať
  `recaptchaToken`. Takýto hlas sa **nededuplikuje** a nedostáva body.
- `verified_accounts_only: true` - len prihlásený s overeným účtom.
- inak - akýkoľvek prihlásený.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_INACTIVE, INVALID_QUESTIONS, DUPLICATE_OPTIONS, INSUFFICIENT_OPTIONS, MISSING_REQUIRED_STATEMENTS, TOO_MANY_OPTIONS, INVALID_OPTIONS, INVALID_SCALE_VALUE, SCALE_TEXT_NOT_ALLOWED, VALUE_NOT_ALLOWED
- 401: LOGIN_REQUIRED
- 403: VERIFIED_ACCOUNT_REQUIRED, RECAPTCHA_VERIFICATION_FAILED
- 404: VOTING_NOT_FOUND
- 409: VOTING_ALREADY_VOTED
- 410: VOTING_FORGOTTEN

    * @method
    * @name DigiobcanApi#submitVote
    */
  submitVote(
    votingId: string,
    parameters: {} = {},
    body: SubmitVoteRequest,
  ): Promise<{
    message?: string;
  }> {
    let path = "/votings/{votingId}/vote";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Get list of all votings, sorted by open_from and voting_id, paginated by limit and pagecursor.

Chyby, ktoré môže endpoint vrátiť:
- 400: INVALID_PAGE_CURSOR

    * @method
    * @name DigiobcanApi#adminGetVotings
    */
  adminGetVotings(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    votings: Array<VotingListItem>;
  }> {
    let path = "/admin/votings";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Create a new voting

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_INVALID_DATES, VOTING_INVALID_END_DATE, VOTING_INVALID_AUDIENCE, VOTING_INVALID_OPTIONS_RANGE, VOTING_INSUFFICIENT_OPTIONS, INVALID_OPTION_URL, VOTING_INVALID_SCALE, VOTING_INVALID_SCALE_MIN, VOTING_SCALE_NOT_ALLOWED, VOTING_SCALE_NO_STATEMENTS, VOTING_SCALE_FREE_OPTION, VOTING_REQUIRED_NOT_ALLOWED
- 500: VOTING_CREATION_FAILED

    * @method
    * @name DigiobcanApi#adminCreateVoting
    */
  adminCreateVoting(
    parameters: {} = {},
    body: CreateVotingRequest,
  ): Promise<{
    voting_id: string;
  }> {
    let path = "/admin/votings";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Get voting detail by ID

Chyby, ktoré môže endpoint vrátiť:
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminGetVotingDetail
    */
  adminGetVotingDetail(
    votingId: string,
    parameters: {} = {},
  ): Promise<VotingDetail> {
    let path = "/admin/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Update voting

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_ALREADY_ACTIVE, VOTING_INVALID_DATES, VOTING_INVALID_AUDIENCE, VOTING_INVALID_OPTIONS_RANGE, VOTING_INSUFFICIENT_OPTIONS, INVALID_OPTION_URL, VOTING_INVALID_SCALE, VOTING_INVALID_SCALE_MIN, VOTING_SCALE_NOT_ALLOWED, VOTING_SCALE_NO_STATEMENTS, VOTING_SCALE_FREE_OPTION, VOTING_REQUIRED_NOT_ALLOWED, VOTING_UPDATE_FAILED
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminUpdateVoting
    */
  adminUpdateVoting(
    votingId: string,
    parameters: {} = {},
    body: CreateVotingRequest,
  ): Promise<VotingDetail> {
    let path = "/admin/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Update voting status (open/close)

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_INVALID_STATUS_UPDATE, VOTING_INACTIVE, VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminUpdateVotingStatus
    */
  adminUpdateVotingStatus(
    votingId: string,
    parameters: {} = {},
    body: {
      status: "open" | "closed";
    },
  ): Promise<{
    message?: string;
  }> {
    let path = "/admin/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Archivovanie hlasovania (GDPR).

Zmaže `votings.secret`, všetky záznamy o účasti a `voter_key` spolu
s voľnými odpoveďami (`votings_results.text`). Dôsledky, ktoré treba
povedať pred stlačením: hlasovanie zmizne občanom zo zoznamu aj
z detailu, **hlasujúci si svoje odpovede už nepozrú** a voľné odpovede
sú nenávratne zmazané. Agregované výsledky (počty, histogramy škál)
zostávajú.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_ALREADY_FORGOTTEN
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminForgetVoting
    */
  adminForgetVoting(
    votingId: string,
    parameters: {} = {},
  ): Promise<{
    message?: string;
  }> {
    let path = "/admin/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Viditeľnosť hlasovania pre občana: či detail vidia aj neprihlásení
(`public`) a či a kedy sa zverejňujú podrobné výsledky
(`available_results`, `available_incomplete_results`).

**Vlastný endpoint, lebo `PUT /admin/votings/{votingId}` sa po otvorení
hlasovania odmieta** (`VOTING_ALREADY_ACTIVE`) - a to je práve stav,
v ktorom sa mesto o zverejnení výsledkov rozhoduje. Obsah hlasovania
(otázky, dátumy, publikum) tento endpoint nemení; mení len to, čo z už
odovzdaných hlasov smie občan vidieť.

Posielať sa dá aj jeden kľúč - neuvedené zostávajú, ako boli.
`available_results: false` strhne so sebou aj
`available_incomplete_results` (priebežné výsledky nezverejneného
hlasovania nedávajú zmysel a `CHECK` v databáze ich nepustí).
Kombinácia „nezverejňovať, ale priebežne áno" poslaná naraz je chyba,
nie tichá oprava.

Archivované (zabudnuté) hlasovanie sa prepnúť nedá - jeho podrobné
výsledky sa nezobrazujú tak či tak.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_INVALID_RESULTS_VISIBILITY, VOTING_ALREADY_FORGOTTEN
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminUpdateVotingVisibility
    */
  adminUpdateVotingVisibility(
    votingId: string,
    parameters: {} = {},
    body: {
      public?: boolean;
      available_results?: boolean;
      available_incomplete_results?: boolean;
    },
  ): Promise<{
    public: boolean;
    available_results: boolean;
    available_incomplete_results: boolean;
  }> {
    let path = "/admin/votings/{votingId}/visibility";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Dátum uzavretia už otvoreného hlasovania: posunúť ho do budúcnosti
alebo ho zrušiť celkom (hlasovanie potom beží, kým ho niekto ručne
neuzavrie).

**Vlastný endpoint z tej istej príčiny ako `/visibility`:**
`PUT /admin/votings/{votingId}` sa po otvorení hlasovania odmieta
(`VOTING_ALREADY_ACTIVE`) - a predlžuje sa práve vtedy, keď
hlasovanie beží. Obsah hlasovania (otázky, publikum, viditeľnosť)
tento endpoint nemení, takže sa cez termín nedá obísť zámok nad
bežiacim hlasovaním.

**`open_from` v tele nie je a meniť sa nedá.** Hlasovanie už beží
a odovzdané hlasy sa k jeho začiatku vzťahujú; pole, ktoré server
zahodí, sa preto do kontraktu nedáva.

**Je to `PUT`, nie `PATCH`, a telo je celý rozvrh:** chýbajúci
(alebo `null`) `open_to` znamená „bez dátumu konca", nie
„nechaj, ako bolo". Susedný `PATCH /visibility` má opačnú
konvenciu - neuvedený kľúč tam zostáva - a dva endpointy nad tým
istým hlasovaním nesmú vyzerať rovnako a znamenať niečo iné.

Endpoint platí **len na otvorené hlasovanie**. Draft (`open_from`
chýba alebo je v budúcnosti) sa mení celý cez
`PUT /admin/votings/{votingId}`, uzavreté hlasovanie sa už
neotvára - oba stavy sú `VOTING_NOT_OPEN`. Nový `open_to` musí byť
v budúcnosti: zápisom do minulosti by sa hlasovanie zavrelo a na to
je `PATCH /admin/votings/{votingId}` so `status: closed`.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_NOT_OPEN, VOTING_INVALID_DATES, VOTING_ALREADY_FORGOTTEN
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminUpdateVotingSchedule
    */
  adminUpdateVotingSchedule(
    votingId: string,
    parameters: {} = {},
    body: {
      open_to?: string;
    },
  ): Promise<{
    open_from: string;
    open_to?: string;
  }> {
    let path = "/admin/votings/{votingId}/schedule";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Nastavenie reportu: či sa má po uzavretí hlasovania odoslať a komu.

**Vlastný endpoint z tej istej príčiny ako `/visibility` a `/schedule`:**
`PUT /admin/votings/{votingId}` sa po otvorení hlasovania odmieta
(`VOTING_ALREADY_ACTIVE`), a o tom, komu report pošleme, sa mesto
rozhoduje práve vtedy, keď hlasovanie beží. Obsah hlasovania sa tým
nemení.

**Je to `PUT` a telo je celé nastavenie**, rovnako ako `/schedule`:
`report_emails` sa nahradí celý zoznam, nie po jednej adrese.

Adresáti **nie sú účty aplikácie**. Report chodí komisii, poslancovi
alebo externému spracovateľovi, takže sa neoveruje, či adresa patrí
používateľovi - overuje sa len jej tvar.

Zapnutý report bez adries je `VOTING_REPORT_NO_RECIPIENTS`: nemal by
kam odísť a jediné, čo by sa stalo, je že by mail nikdy neprišiel
a nikto by sa to nedozvedel. Tú istú kombináciu odmieta aj CHECK
v databáze.

Na **archivovanom** hlasovaní sa nastaviť nedá: archivácia výsledky
vypína nenávratne, takže by report nemal čo vygenerovať.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_ALREADY_FORGOTTEN, VOTING_REPORT_NO_RECIPIENTS, VOTING_REPORT_INVALID_EMAIL
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminUpdateVotingReport
    */
  adminUpdateVotingReport(
    votingId: string,
    parameters: {} = {},
    body: {
      report_enabled: boolean;
      report_emails?: Array<string>;
    },
  ): Promise<{
    report_enabled: boolean;
    report_emails: Array<string>;
    report_sent_at?: string;
  }> {
    let path = "/admin/votings/{votingId}/report";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "PUT",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
  /**
    * Stiahnutie reportu - ten istý dokument, aký odchádza mailom.

Skladá sa **pri každom volaní nanovo** z aktuálnych výsledkov, nič sa
neukladá do medzipamäte. Dá sa preto stiahnuť aj z bežiaceho
hlasovania a čísla v ňom platia k okamihu stiahnutia.

Odpoveď je **binárna** (`application/pdf`, alebo `text/csv` pri
`format=csv`), nie JSON, plus `Content-Disposition` s menom súboru
`hlasovanie-<ID>.pdf` / `.csv`.

Archivované hlasovanie report nevydá (`VOTING_ALREADY_FORGOTTEN`) -
archivácia maže texty voľných odpovedí a vypína výsledky aj pre
administráciu, takže by report bol jediné miesto, kde sa dajú ešte
prečítať.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_ALREADY_FORGOTTEN
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminDownloadVotingReport
    */
  adminDownloadVotingReport(
    votingId: string,
    parameters: {
      format?: "pdf" | "csv";
    } = {},
  ): Promise<File | string> {
    let path = "/admin/votings/{votingId}/report";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/pdf");
    headers.append("Accept", "text/csv");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    queryParameters["format"] = "pdf";

    if (typeof parameters["format"] !== "undefined") {
      queryParameters["format"] = parameters["format"];
    }

    queryParameters["format"] = this.transformParameter(
      queryParameters["format"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Odoslať report teraz, bez čakania na uzavretie hlasovania.

Slúži na dve veci: overiť, že mail naozaj chodí a ako vyzerá, a znova
poslať report, keď niekomu nedošiel.

**Razítko `report_sent_at` sa nastaví len pri uzavretom hlasovaní.**
Keby ho nastavilo aj bežiace, plánovaná úloha by hlasovanie po
uzavretí preskočila a mesto by dostalo report s priebežnými číslami
namiesto konečných.

Adresy sa berú z nastavenia (`report_emails`); zapnutý prepínač
`report_enabled` sa nevyžaduje, aby sa dal report vyskúšať ešte pred
jeho zapnutím. Bez adries je to `VOTING_REPORT_NO_RECIPIENTS`.

Chyby, ktoré môže endpoint vrátiť:
- 400: VOTING_ALREADY_FORGOTTEN, VOTING_REPORT_NO_RECIPIENTS
- 404: VOTING_NOT_FOUND

    * @method
    * @name DigiobcanApi#adminSendVotingReport
    */
  adminSendVotingReport(
    votingId: string,
    parameters: {} = {},
  ): Promise<{
    recipients: number;
    report_sent_at?: string;
  }> {
    let path = "/admin/votings/{votingId}/report/send";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Zabudnutie hlasu jedného človeka (GDPR, čl. 17).

Maže **aj samotné odpovede**, nie len záznam o účasti. Predtým to
nešlo - `votings_results` nemal ako riadky priradiť - a endpoint tak
vedel odstrániť len väzbu, čo žiadosti o výmaz obsahu hlasu nevyhovelo.
Odpovede sa nájdu cez `voter_key`, ktorý sa odvádza z `clientAccId`;
preto sa v ceste **od verzie 6.0.0 posiela ID účtu, nie hash účasti**.
Z hashu sa kľúč odvodiť nedá a volajúci ID účtu aj tak musel mať, aby si
hash vôbec vyrobil.

Čo endpoint nezmaže: anonymné hlasy (nemajú `voter_key`, takže sa
nedajú priradiť nikomu) a lístky odovzdané pred migráciou 000020.

Zmazaním záznamu o účasti zaniká aj deduplikačná zábrana, takže ten
človek v hlasovaní môže hlasovať znova. Je to zámerné - po výmaze hlas
nemá - ale treba o tom vedieť.

Chyby, ktoré môže endpoint vrátiť:
- 404: VOTING_NOT_FOUND, PARTICIPATION_NOT_FOUND
- 410: VOTING_FORGOTTEN

    * @method
    * @name DigiobcanApi#adminForgetUserVote
    */
  adminForgetUserVote(
    votingId: string,
    clientAccId: string,
    parameters: {} = {},
  ): Promise<{
    message?: string;
  }> {
    let path = "/admin/votings/{votingId}/participants/{clientAccId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    path = path.replace("{clientAccId}", clientAccId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * List all projects as an admin
   * @method
   * @name DigiobcanApi#adminGetProjects
   */
  adminGetProjects(
    parameters: {
      limit?: number;
      pagecursor?: Array<string>;
    } = {},
  ): Promise<{
    projects: Array<ProjectListItem>;
  }> {
    let path = "/admin/projects";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["pagecursor"] !== "undefined") {
      queryParameters["pagecursor"] = parameters["pagecursor"];
    }

    queryParameters["pagecursor"] = this.transformParameter(
      queryParameters["pagecursor"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Create a new project
   * @method
   * @name DigiobcanApi#adminCreateProject
   */
  adminCreateProject(
    parameters: {} = {},
    form: NewProject,
  ): Promise<{
    project_id: string;
  }> {
    let path = "/admin/projects";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
   * Get project detail as an admin
   * @method
   * @name DigiobcanApi#adminGetProjectDetail
   */
  adminGetProjectDetail(
    projectId: string,
    parameters: {} = {},
  ): Promise<ProjectDetail> {
    let path = "/admin/projects/{projectId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{projectId}", projectId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Update project detail
   * @method
   * @name DigiobcanApi#adminUpdateProject
   */
  adminUpdateProject(
    projectId: string,
    parameters: {} = {},
    form: ProjectUpdate,
  ): Promise<void> {
    let path = "/admin/projects/{projectId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");

    path = path.replace("{projectId}", projectId.toString());

    return this.request(
      "PATCH",
      `${this.baseUrl}${path}`,
      form,
      headers,
      queryParameters,
    );
  }
  /**
   * Delete project
   * @method
   * @name DigiobcanApi#adminDeleteProject
   */
  adminDeleteProject(projectId: string, parameters: {} = {}): Promise<void> {
    let path = "/admin/projects/{projectId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{projectId}", projectId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
   * Delete project media
   * @method
   * @name DigiobcanApi#adminDeleteProjectMedia
   */
  adminDeleteProjectMedia(
    projectId: string,
    mediaId: string,
    parameters: {} = {},
  ): Promise<void> {
    let path = "/admin/projects/{projectId}/media/{mediaId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    path = path.replace("{projectId}", projectId.toString());

    path = path.replace("{mediaId}", mediaId.toString());

    return this.request(
      "DELETE",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * `index.html` frontendu s meta značkami podnetu (titul, popis,
`og:url` na `/podnety/<ID>`; obrázok ostáva predvolený náhľad
appky) - náhľad pri zdieľaní na
sociálnych sieťach, ktorých roboty JavaScript nespúšťajú.

Nevolá ho prehliadač, ale nginx frontendu pre cestu `/podnety/<ID>`.
Pri chybe podá nginx obyčajné `index.html`, takže odkaz funguje aj
bez tohto endpointu. Podnet, ktorý verejne vidieť nie je (nevhodný,
archivovaný, neexistuje), dostane `index.html` bez zmeny.

    * @method
    * @name DigiobcanApi#getReportSharePage
    */
  getReportSharePage(reportId: string, parameters: {} = {}): Promise<string> {
    let path = "/share/reports/{reportId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "text/html");
    headers.append("Accept", "application/json");

    path = path.replace("{reportId}", reportId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * `index.html` frontendu s meta značkami hlasovania (titul
`Hlasovanie: <názov>`, popis bez markdownu, `og:url` na
`/hlasovania/<ID>`; obrázok ostáva predvolený náhľad appky) - náhľad
pri zdieľaní na sociálnych sieťach, ktorých roboty JavaScript
nespúšťajú. Rovnako ako `/share/reports/{reportId}` ho volá nginx
frontendu; hlasovanie, ktoré neprihlásený nevidí (neverejné,
nenaplánované, zabudnuté), dostane `index.html` bez zmeny.

    * @method
    * @name DigiobcanApi#getVotingSharePage
    */
  getVotingSharePage(votingId: string, parameters: {} = {}): Promise<string> {
    let path = "/share/votings/{votingId}";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "text/html");
    headers.append("Accept", "application/json");

    path = path.replace("{votingId}", votingId.toString());

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Hranica mesta Prešov ako GeoJSON polygón vo WGS84 (EPSG:4326).

Verejné a bez tokenu - je to statická referenčná geometria, z ktorej si
frontend kreslí masku na mape a predbežne overuje polohu podnetu ešte
pred odoslaním. Server podľa toho istého polygónu rozhoduje aj v
`POST /me/reports`, takže sa mapa a API nemôžu rozísť v tom, čo je
"na území mesta".

Polygón je hranica mesta rozšírená o ~100 m von, aby podnet na ceste,
ktorá sama tvorí hranicu, neskončil na 400.

    * @method
    * @name DigiobcanApi#getCityBoundary
    */
  getCityBoundary(parameters: {} = {}): Promise<{
    type: "Polygon";
    coordinates: Array<{}>;
  }> {
    let path = "/geo/boundary";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers.append("Accept", "application/json");

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Vsetky behy AI nad podnetmi - kategorizacia aj moderacia - od
najnovsieho. Jeden prvok je jeden beh jednej ulohy nad jednym
podnetom, vratane opakovanych behov a zlyhani (`error`).

Kazdy beh nesie, co povedal model, co z toho urobil backend a ako
stav podnetu vyzera **dnes** - z rozdielu je vidno, kde uradnik AI
prepisal (`humanAgrees`).

    * @method
    * @name DigiobcanApi#adminGetAiDecisions
    */
  adminGetAiDecisions(
    parameters: {
      limit?: number;
      offset?: number;
      type?: Array<"categories" | "explicit" | "duplicates">;
      model?: Array<string>;
      promptVersion?: Array<string>;
      onlyErrors?: boolean;
      reportId?: string;
      dryRun?: boolean;
      dateFrom?: string;
      dateTo?: string;
    } = {},
  ): Promise<{
    decisions: Array<AiDecision>;
    hasNextOffset: boolean;
    offset: number;
    limit: number;
  }> {
    let path = "/admin/ai-decisions";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    queryParameters["limit"] = 100;

    if (typeof parameters["limit"] !== "undefined") {
      queryParameters["limit"] = parameters["limit"];
    }

    queryParameters["limit"] = this.transformParameter(
      queryParameters["limit"],
    );

    if (typeof parameters["offset"] !== "undefined") {
      queryParameters["offset"] = parameters["offset"];
    }

    queryParameters["offset"] = this.transformParameter(
      queryParameters["offset"],
    );

    if (typeof parameters["type"] !== "undefined") {
      queryParameters["type"] = parameters["type"];
    }

    queryParameters["type"] = this.transformParameter(queryParameters["type"]);

    if (typeof parameters["model"] !== "undefined") {
      queryParameters["model"] = parameters["model"];
    }

    queryParameters["model"] = this.transformParameter(
      queryParameters["model"],
    );

    if (typeof parameters["promptVersion"] !== "undefined") {
      queryParameters["promptVersion"] = parameters["promptVersion"];
    }

    queryParameters["promptVersion"] = this.transformParameter(
      queryParameters["promptVersion"],
    );

    if (typeof parameters["onlyErrors"] !== "undefined") {
      queryParameters["onlyErrors"] = parameters["onlyErrors"];
    }

    queryParameters["onlyErrors"] = this.transformParameter(
      queryParameters["onlyErrors"],
    );

    if (typeof parameters["reportId"] !== "undefined") {
      queryParameters["reportId"] = parameters["reportId"];
    }

    queryParameters["reportId"] = this.transformParameter(
      queryParameters["reportId"],
    );

    if (typeof parameters["dryRun"] !== "undefined") {
      queryParameters["dryRun"] = parameters["dryRun"];
    }

    queryParameters["dryRun"] = this.transformParameter(
      queryParameters["dryRun"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Suhrn behov AI podla typu ulohy, modelu a verzie promptu - s tymi
istymi filtrami ako `GET /admin/ai-decisions`, takze pas cisel nad
zoznamom hovori o tom istom vybere ako tabulka.

`humanAgreed` / `humanDisagreed` pocitaju len behy bez chyby, pri
ktorych sa zhoda da urcit (vid `AiDecision.humanAgrees`).

    * @method
    * @name DigiobcanApi#adminGetAiDecisionsStats
    */
  adminGetAiDecisionsStats(
    parameters: {
      type?: Array<"categories" | "explicit" | "duplicates">;
      model?: Array<string>;
      promptVersion?: Array<string>;
      onlyErrors?: boolean;
      reportId?: string;
      dryRun?: boolean;
      dateFrom?: string;
      dateTo?: string;
    } = {},
  ): Promise<{
    groups: Array<AiDecisionStatsGroup>;
  }> {
    let path = "/admin/ai-decisions-stats";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["type"] !== "undefined") {
      queryParameters["type"] = parameters["type"];
    }

    queryParameters["type"] = this.transformParameter(queryParameters["type"]);

    if (typeof parameters["model"] !== "undefined") {
      queryParameters["model"] = parameters["model"];
    }

    queryParameters["model"] = this.transformParameter(
      queryParameters["model"],
    );

    if (typeof parameters["promptVersion"] !== "undefined") {
      queryParameters["promptVersion"] = parameters["promptVersion"];
    }

    queryParameters["promptVersion"] = this.transformParameter(
      queryParameters["promptVersion"],
    );

    if (typeof parameters["onlyErrors"] !== "undefined") {
      queryParameters["onlyErrors"] = parameters["onlyErrors"];
    }

    queryParameters["onlyErrors"] = this.transformParameter(
      queryParameters["onlyErrors"],
    );

    if (typeof parameters["reportId"] !== "undefined") {
      queryParameters["reportId"] = parameters["reportId"];
    }

    queryParameters["reportId"] = this.transformParameter(
      queryParameters["reportId"],
    );

    if (typeof parameters["dryRun"] !== "undefined") {
      queryParameters["dryRun"] = parameters["dryRun"];
    }

    queryParameters["dryRun"] = this.transformParameter(
      queryParameters["dryRun"],
    );

    if (typeof parameters["dateFrom"] !== "undefined") {
      queryParameters["dateFrom"] = parameters["dateFrom"];
    }

    queryParameters["dateFrom"] = this.transformParameter(
      queryParameters["dateFrom"],
    );

    if (typeof parameters["dateTo"] !== "undefined") {
      queryParameters["dateTo"] = parameters["dateTo"];
    }

    queryParameters["dateTo"] = this.transformParameter(
      queryParameters["dateTo"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Ulohy AI nad podnetmi, ktore este nedobehli - jeden prvok je jedna
uloha (kategorizacia alebo moderacia) jedneho podnetu. Ked uloha
dobehne, z tohto zoznamu zmizne a v `GET /admin/ai-decisions`
pribudne jej rozhodnutie.

Radenie: najprv beziace (`running`, od najstarsieho startu), potom
cakajuce (`queued`) v poradi, v akom na ne pride rad. Zoznam nie je
strankovany, vrati najviac 200 prvkov.

Filtre su tie iste ako pri rozhodnutiach, okrem tych, ktore o
nedobehnutom behu nedavaju zmysel (chyba, datum).

    * @method
    * @name DigiobcanApi#adminGetAiRuns
    */
  adminGetAiRuns(
    parameters: {
      type?: Array<"categories" | "explicit" | "duplicates">;
      model?: Array<string>;
      promptVersion?: Array<string>;
      reportId?: string;
      dryRun?: boolean;
    } = {},
  ): Promise<{
    runs: Array<AiRun>;
  }> {
    let path = "/admin/ai-runs";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Accept", "application/json");

    if (typeof parameters["type"] !== "undefined") {
      queryParameters["type"] = parameters["type"];
    }

    queryParameters["type"] = this.transformParameter(queryParameters["type"]);

    if (typeof parameters["model"] !== "undefined") {
      queryParameters["model"] = parameters["model"];
    }

    queryParameters["model"] = this.transformParameter(
      queryParameters["model"],
    );

    if (typeof parameters["promptVersion"] !== "undefined") {
      queryParameters["promptVersion"] = parameters["promptVersion"];
    }

    queryParameters["promptVersion"] = this.transformParameter(
      queryParameters["promptVersion"],
    );

    if (typeof parameters["reportId"] !== "undefined") {
      queryParameters["reportId"] = parameters["reportId"];
    }

    queryParameters["reportId"] = this.transformParameter(
      queryParameters["reportId"],
    );

    if (typeof parameters["dryRun"] !== "undefined") {
      queryParameters["dryRun"] = parameters["dryRun"];
    }

    queryParameters["dryRun"] = this.transformParameter(
      queryParameters["dryRun"],
    );

    return this.request(
      "GET",
      `${this.baseUrl}${path}`,
      {},
      headers,
      queryParameters,
    );
  }
  /**
    * Posle vybrane podnety do reports-analyzer na skusobny beh AI.
Rozhodnutie sa zapise do `GET /admin/ai-decisions` s `dryRun: true`,
ale **neuplatni sa**: podnet nedostane novy nalez, nezmeni sa jeho
viditelnost, uradnici nedostanu notifikaciu a obcan body.

Odpoved prichadza hned, behy bezia asynchronne a po jednom (na
zdielanej GPU zhruba desiatky sekund na podnet s fotkami), takze
vysledky v zozname pribudaju postupne.

    * @method
    * @name DigiobcanApi#adminCreateAiDecisionsDryRun
    */
  adminCreateAiDecisionsDryRun(
    parameters: {} = {},
    body: {
      reportIds: Array<string>;
      tasks: Array<"categories" | "explicit">;
    },
  ): Promise<{
    queued: number;
  }> {
    let path = "/admin/ai-decisions-dry-run";
    let headers: Headers = new Headers();
    let queryParameters: QueryParameters = {};

    headers = this.appendAuthHeaders(headers);
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");

    return this.request(
      "POST",
      `${this.baseUrl}${path}`,
      body,
      headers,
      queryParameters,
    );
  }
}

const client = new DigiobcanApi();
client.setBaseUrl(config.DIGIOBCAN_API_URL);
export default networkErrorApiWrapper(urnApiWrapper(client, 'DigiobcanApi'));
export { DigiobcanApi };
